import { UserPicker } from "../components/UserPicker";
import { ArrowLeft, Copy, Eye, MoreHorizontal, Pencil, Plus, Search, Sparkles, Trash2, Upload, UserPlus, Users, X } from "lucide-react";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AdditionalPermission, AdditionalPermissionGrant, Announcement, api, AuditEvent, Board, BoardCommand, Department, DepartmentMembershipCommand, ReportSchema, ReportSchemaFieldCommand, ReportSchemaStageCommand, Role, Session,
  TaskSchema, TaskSchemaFieldCommand, TenantSettings, User,
} from "../api";
import { initials, koDate, koStamp, koTime } from "../lib/format";
import { BlockEditor, DocumentBlock, paragraph } from "../components/block-editor";
import { departmentConfiguredActive, departmentDescendants, DepartmentOptions, DepartmentTree, primaryRole, UNASSIGNED, userBelongsTo, userDepartmentIds, userDepartmentMemberships, userManagesDepartment } from "../lib/org";
import { Avatar, ConfirmDialog, DataTable, Empty, Loading, ModalFrame, PageHead, RequirementHint, focusInvalidField, SpineField, Toggle } from "../components/ui";
import type { SettingsSection } from "../lib/types";
import { AttendancePolicySettings } from "./AttendanceManagement";
import { EvaluationCriteriaSettings } from "./Evaluation";
import { HolidayCalendarSettings, LeavePolicySettings } from "./People";

const roleText: Record<Role, string> = { OWNER: "조직 관리자", MANAGER: "매니저", MEMBER: "구성원" };

const sections: { id: SettingsSection; labelKey: string }[] = [
  { id: "system", labelKey: "settings.general" }, { id: "org", labelKey: "settings.organization" }, { id: "permissions", labelKey: "settings.permissions" }, { id: "attendance", labelKey: "settings.attendance" }, { id: "work", labelKey: "settings.work" }, { id: "evaluation", labelKey: "settings.evaluation" }, { id: "logs", labelKey: "settings.logs" },
];
/** ref의 로그는 종류를 한국어로 보여 준다. 없는 코드는 그대로 노출해 새 이벤트를 놓치지 않는다. */
const auditActionText: Record<string, string> = {
  LOGIN_SUCCEEDED: "로그인", LOGIN_FAILED: "로그인 실패", ACCOUNT_LOCKED: "계정 잠금", PASSWORD_CHANGED: "비밀번호 변경",
  SESSION_REVOKED: "세션 종료", SESSION_REISSUED_AFTER_PASSWORD_CHANGE: "세션 재발급", REFRESH_TOKEN_REUSE_DETECTED: "토큰 재사용 감지",
  USER_INVITED: "구성원 초대", USER_ACCESS_CHANGED: "구성원 변경", USER_DEPARTED: "구성원 퇴사",
  USER_PROFILE_CHANGED: "개인 이름 변경", USER_AVATAR_CHANGED: "프로필 이미지 변경",
  USER_ADDITIONAL_PERMISSIONS_REPLACED: "권한 변경",
  DEPARTMENT_CREATED: "팀 추가", DEPARTMENT_MOVED: "팀 이동", DEPARTMENT_STATUS_CHANGED: "팀 상태", DEPARTMENT_LEADER_CHANGED: "매니저 변경", DEPARTMENT_DEFAULT_MANAGER_CHANGED: "기본 관리자 지정", DEPARTMENT_DELETED: "팀 삭제",
  TASK_DRAFT_CREATED: "지시 초안", TASK_DRAFT_UPDATED: "지시 초안 수정", TASK_CONFIRMED: "지시 발행", TASK_STARTED: "업무 착수", TASK_SUBMITTED: "결과 제출",
  TASK_VERIFICATION_STARTED: "검증 시작", TASK_VERIFICATION_SUBMITTED: "검증 제출",
  TASK_CANCELLED: "업무 취소", TASK_COMMENTED: "의견", TASK_COMMENT_CREATED: "의견",
  QUALITY_REVIEW_SUBMITTED: "품질평가", PERFORMANCE_EXPORTED: "집계 내보내기",
  TASK_SCHEMA_CREATED: "작업지시 스키마", TASK_SCHEMA_VERSION_CREATED: "양식 변경", TASK_CONFIGURATION_STATUS_CHANGED: "업무 설정 상태",
  EVALUATION_CRITERIA_VERSION_CREATED: "평가 기준",
  PROJECT_CREATED: "프로젝트 생성", PROJECT_UPDATED: "프로젝트 변경", PROJECT_WORKFLOW_REPLACED: "워크플로우 변경",
  PROJECT_WORKFLOW_DEACTIVATED: "워크플로우 중지", PROJECT_MEMBERS_REPLACED: "프로젝트 구성원", PROJECT_DEPARTMENTS_REPLACED: "프로젝트 부서",
  PROJECT_LINK_ADDED: "문서 링크", PROJECT_FILE_ADDED: "문서 파일", PROJECT_RESOURCE_REMOVED: "문서 삭제",
  RESEARCH_NOTE_CREATED: "연구노트 생성", RESEARCH_NOTE_REVISED: "연구노트 수정", RESEARCH_NOTE_FINALIZED: "연구노트 확정",
  RESEARCH_NOTE_REVIEWED: "연구노트 점검 승인", RESEARCH_NOTE_REJECTED: "연구노트 반려", RESEARCH_NOTE_ANCHORED: "연구노트 앵커", RESEARCH_NOTE_POLICY_UPDATED: "연구노트 정책",
  RESEARCH_NOTE_ATTACHMENT_ADDED: "연구노트 첨부", RESEARCH_NOTE_ATTACHMENT_REMOVED: "연구노트 첨부 삭제",
  FILE_UPLOADED: "파일 업로드", FILE_DOWNLOADED: "파일 다운로드", FILE_ATTACHED: "파일 첨부",
  LEAVE_REQUESTED: "휴가 신청", LEAVE_WITHDRAWN: "휴가 철회", LEAVE_APPROVED: "휴가 승인", LEAVE_REJECTED: "휴가 반려", LEAVE_BALANCE_ADJUSTED: "잔여 휴가 조정",
  ATTENDANCE_HOLIDAY_CREATED: "휴일 추가", ATTENDANCE_HOLIDAY_UPDATED: "휴일 변경", ATTENDANCE_HOLIDAY_DELETED: "휴일 삭제",
  MCP_TOOL_CALLED: "MCP 호출",
  TENANT_PROFILE_CHANGED: "회사 정보", TENANT_CONFIGURATION_CHANGED: "테넌트 설정", TENANT_WORKFLOW_CHANGED: "업무 완료 흐름", MODULE_DISABLED: "기능 사용",
};

const inputTypeText: Record<TaskSchemaFieldCommand["inputType"], string> = {
  SHORT_TEXT: "단문 텍스트", LONG_TEXT: "장문 텍스트", NUMBER: "숫자", SELECT: "선택", DATE_RANGE: "날짜 범위", USER: "구성원", FILE: "파일", CHECKBOX: "체크",
};

export function SettingsPage({ session, permissions = [], section, settings, departments, users, onSection, onChanged, accountOnly = false }: {
  session: Session; permissions?: AdditionalPermission[]; section: SettingsSection; settings: TenantSettings | null; departments: Department[]; users: User[];
  onSection: (next: SettingsSection) => void; onChanged: (message: string) => Promise<void>; accountOnly?: boolean;
}) {
  const { t } = useTranslation();
  const admin = session.roles.includes("OWNER");
  if (accountOnly) return <>
    <PageHead title={t("settings.accountTitle")} meta={t("settings.accountMeta")} />
    <OrganizationSettings session={session} permissions={permissions} admin={false} settings={settings} departments={departments} users={users} onChanged={onChanged} />
  </>;
  const adminOnly = ["permissions", "attendance", "logs"];
  const visibleSections = admin ? sections : sections.filter((item) => !adminOnly.includes(item.id));
  const attendanceEnabled = settings?.modules.includes("ATTENDANCE") ?? false;
  const activeSection = (!admin && adminOnly.includes(section)) || (section === "attendance" && !attendanceEnabled) ? "system" : section;
  return <>
    <PageHead title={t("settings.title")} meta={t("settings.meta")} />
    <nav className="section-nav">{visibleSections.map((item) => {
      const disabled = item.id === "attendance" && !attendanceEnabled;
      return <button type="button" key={item.id} disabled={disabled} title={disabled ? t("settings.attendanceDisabled") : undefined} className={activeSection === item.id ? "on" : ""} onClick={() => onSection(item.id)}>{t(item.labelKey)}</button>;
    })}</nav>
    {activeSection === "system" && <SystemSettings session={session} settings={settings} admin={admin} onChanged={onChanged} />}
    {activeSection === "org" && <OrganizationSettings session={session} permissions={permissions} admin={admin} settings={settings} departments={departments} users={users} onChanged={onChanged} />}
    {activeSection === "permissions" && <><AdditionalPermissionSettings users={users} onChanged={onChanged} /><PolicySettings settings={settings} onChanged={onChanged} /></>}
    {activeSection === "attendance" && attendanceEnabled && <AttendanceSettings onChanged={onChanged} />}
    {activeSection === "work" && <WorkSettings admin={admin} hideTaskForms onChanged={onChanged} />}
    {activeSection === "evaluation" && <EvaluationCriteriaSettings onChanged={onChanged} />}
    {activeSection === "logs" && <AuditSettings users={users} />}
  </>;
}

function AttendanceSettings({ onChanged }: { onChanged: (message: string) => Promise<void> }) {
  const [tab, setTab] = useState<"work" | "leave" | "holiday">("work");
  return <>
    <nav className="attendance-tabs settings-attendance-tabs" aria-label="근태 설정 항목">
      <button type="button" className={tab === "work" ? "active" : ""} onClick={() => setTab("work")}>근무 정책</button>
      <button type="button" className={tab === "leave" ? "active" : ""} onClick={() => setTab("leave")}>휴가 정책</button>
      <button type="button" className={tab === "holiday" ? "active" : ""} onClick={() => setTab("holiday")}>휴일 캘린더</button>
    </nav>
    {tab === "work" && <AttendancePolicySettings onChanged={onChanged} />}
    {tab === "leave" && <LeavePolicySettings />}
    {tab === "holiday" && <HolidayCalendarSettings />}
  </>;
}

/**
 * 게시판 관리. OWNER 또는 게시판 관리 권한을 가진 구성원만 이 화면을 본다.
 *
 * 레거시는 화면에서만 가려 두고 API 는 열려 있었다. 여기서는 서버가 막으므로 화면은
 * 편의를 위해서만 가린다 — 우회해도 통하지 않는다.
 */
export function NoticeManagementPage({ departments, onChanged }: { users: User[]; departments: Department[]; onChanged: (message: string) => Promise<void> }) {
  const [boards, setBoards] = useState<Board[] | null>(null);
  const [editingBoard, setEditingBoard] = useState<Board | "new" | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const loadBoards = useCallback(async () => setBoards(await api.managedBoards()), []);
  useEffect(() => { void loadBoards().catch(reason => setError(reason instanceof Error ? reason.message : "게시판을 불러오지 못했습니다.")); }, [loadBoards]);

  return <>
    <PageHead title="게시판 관리" meta="게시판별 읽기·쓰기 조직과 댓글 사용 여부를 관리합니다. 게시글은 게시판에서 작성하고 관리하세요."
      controls={<button type="button" className="primary" onClick={() => setEditingBoard("new")}><Plus size={14} /> 게시판 추가</button>} />
    {error && <div className="global-error">{error}</div>}
    <section className="section-card">
      <header><div><span className="mono-label">BOARDS</span><h2>게시판 설정</h2></div></header>
      {!boards ? <Loading layout="table" label="게시판을 불러오는 중" /> : <div className="data-table board-management-list">
        <div className="data-row data-head"><span>게시판</span><span>읽기</span><span>쓰기</span><span>댓글</span><span></span></div>
        {boards.map(board => <div className="data-row" key={board.id}>
          <span><b>{board.name}</b><small>{board.description}</small></span>
          <span>{board.readScope === "ALL_MEMBERS" ? "전 구성원" : `${board.readDepartmentIds.length}개 조직`}</span>
          <span>{board.writeScope === "ALL_MEMBERS" ? "전 구성원" : `${board.writeDepartmentIds.length}개 조직`}</span>
          <span>{board.commentsEnabled ? "사용" : "사용 안 함"}</span>
          <span className="row-actions"><button type="button" onClick={() => setEditingBoard(board)}>설정</button></span>
        </div>)}
      </div>}
    </section>
    {editingBoard && <ModalFrame title={editingBoard === "new" ? "게시판 추가" : "게시판 설정"} eyebrow="BOARD SETTINGS" wide onClose={() => setEditingBoard(null)}>
      <BoardForm board={editingBoard === "new" ? null : editingBoard} departments={departments} busy={busy} onCancel={() => setEditingBoard(null)} onSubmit={async command => {
        setBusy(true); setError("");
        try {
          if (editingBoard === "new") await api.createBoard(command); else await api.updateBoard(editingBoard.id, command);
          setEditingBoard(null); await loadBoards(); await onChanged("게시판 설정을 저장했습니다.");
        } catch (reason) { setError(reason instanceof Error ? reason.message : "게시판 설정을 저장하지 못했습니다."); }
        finally { setBusy(false); }
      }} />
    </ModalFrame>}
  </>;
}

export function NoticeForm({ item, busy, onCancel, onSubmit, onError }: {
  item: Announcement | null; busy: boolean;
  onCancel: () => void;
  onSubmit: (body: { title: string; body: string; pinned: boolean; status: Announcement["status"]; blocks: DocumentBlock[] }) => Promise<void>;
  onError: (message: string) => void;
}) {
  const [title, setTitle] = useState(item?.untrustedTitle || "");
  const [blocks, setBlocks] = useState<DocumentBlock[]>(item?.blocks?.length ? item.blocks : [paragraph(item?.untrustedBody || "")]);
  const [pinned, setPinned] = useState(item?.pinned || false);
  const [attempted, setAttempted] = useState(false);
  const hasContent = blocks.some((block) => block.type === "IMAGE" || block.type === "TABLE" || (block.type === "PARAGRAPH" && block.text.trim()));
  const missing = [!title.trim() ? "제목" : "", !hasContent ? "본문" : ""].filter(Boolean);

  const plainBody = blocks.map((block) => {
    if (block.type === "PARAGRAPH") return block.text;
    if (block.type === "TABLE") return [block.columns.join("\t"), ...block.rows.map((row) => row.join("\t"))].join("\n");
    return block.caption || block.file.originalName;
  }).filter(Boolean).join("\n");

  const save = (status: Announcement["status"]) => onSubmit({ title: title.trim(), body: plainBody, pinned, status, blocks });

  return <form className="modal-form" noValidate onSubmit={(event) => {
    event.preventDefault();
    setAttempted(true);
    if (missing.length) { focusInvalidField(event.currentTarget); return; }
    void save("PUBLISHED");
  }}>
    <label className="span-2"><span>제목 <em className="required-mark">필수</em></span>
      <input aria-label="제목" aria-invalid={attempted && !title.trim()} value={title} required maxLength={240} placeholder="예) 하계 정기 점검 안내" onChange={(event) => setTitle(event.target.value)} />
    </label>
    <div className={`span-2 notice-editor-field ${attempted && !hasContent ? "field-missing" : ""}`}><span>본문 <em className="required-mark">필수</em></span>
      <BlockEditor
        ariaLabel="공지 본문"
        value={blocks}
        onChange={setBlocks}
        onUploadImage={api.uploadFile}
        loadImage={api.downloadFile}
        onError={onError}
      />
      <small>블록 추가에서 표와 이미지를 넣을 수 있습니다. 이미지를 선택한 뒤 모서리를 끌어 크기를 조절하세요.</small>
    </div>
    <label className="span-2 checkbox-field">
      <input type="checkbox" checked={pinned} onChange={(event) => setPinned(event.target.checked)} />
      <span>목록 맨 위에 고정</span>
    </label>
    {attempted && <RequirementHint className="span-2" missing={missing} />}
    <footer className="span-2">
      <button type="button" onClick={onCancel}>취소</button>
      <button type="button" disabled={busy} onClick={() => void save("DRAFT")}>{busy ? "저장 중…" : "임시저장"}</button>
      <button type="submit" className="primary" disabled={busy}>{busy ? "처리 중…" : item?.status === "PUBLISHED" ? "변경사항 발행" : "발행"}</button>
    </footer>
  </form>;
}

function BoardForm({ board, departments, busy, onCancel, onSubmit }: {
  board: Board | null; departments: Department[]; busy: boolean; onCancel: () => void; onSubmit: (command: BoardCommand) => Promise<void>;
}) {
  const [name, setName] = useState(board?.name || "");
  const [description, setDescription] = useState(board?.description || "");
  const [readScope, setReadScope] = useState<BoardCommand["readScope"]>(board?.readScope || "ALL_MEMBERS");
  const [writeScope, setWriteScope] = useState<BoardCommand["writeScope"]>(board?.writeScope || "ALL_MEMBERS");
  const [readDepartments, setReadDepartments] = useState(new Set(board?.readDepartmentIds || []));
  const [writeDepartments, setWriteDepartments] = useState(new Set(board?.writeDepartmentIds || []));
  const [commentsEnabled, setCommentsEnabled] = useState(board?.commentsEnabled ?? true);
  const toggleDepartment = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, id: string, checked: boolean) => setter((current) => {
    const next = new Set(current); if (checked) next.add(id); else next.delete(id); return next;
  });
  const available = departments.filter(departmentConfiguredActive);
  const invalid = !name.trim() || (readScope === "DEPARTMENTS" && !readDepartments.size) || (writeScope === "DEPARTMENTS" && !writeDepartments.size);
  return <form className="modal-form board-settings-form" onSubmit={(event) => { event.preventDefault(); if (!invalid) void onSubmit({
    name: name.trim(), description: description.trim(), readScope, writeScope,
    readDepartmentIds: [...readDepartments], writeDepartmentIds: [...writeDepartments], commentsEnabled,
  }); }}>
    <label className="span-2"><span>게시판 이름 <em className="required-mark">필수</em></span><input value={name} maxLength={120} onChange={(event) => setName(event.target.value)} /></label>
    <label className="span-2">설명<textarea value={description} maxLength={500} onChange={(event) => setDescription(event.target.value)} /></label>
    {(["read", "write"] as const).map((kind) => {
      const scope = kind === "read" ? readScope : writeScope;
      const setScope = kind === "read" ? setReadScope : setWriteScope;
      const selected = kind === "read" ? readDepartments : writeDepartments;
      const setter = kind === "read" ? setReadDepartments : setWriteDepartments;
      return <fieldset key={kind} className="board-scope-field"><legend>{kind === "read" ? "읽기 권한" : "쓰기 권한"}</legend>
        <label><input type="radio" name={`board-${kind}-scope`} checked={scope === "ALL_MEMBERS"} onChange={() => setScope("ALL_MEMBERS")} /> 전 구성원</label>
        <label><input type="radio" name={`board-${kind}-scope`} checked={scope === "DEPARTMENTS"} onChange={() => setScope("DEPARTMENTS")} /> 특정 조직</label>
        {scope === "DEPARTMENTS" && <div className="board-department-options">{available.map((department) => <label key={department.id}>
          <input type="checkbox" checked={selected.has(department.id)} onChange={(event) => toggleDepartment(setter, department.id, event.target.checked)} /> {department.name}
        </label>)}</div>}
        <small>상위 조직을 선택하면 하위 조직 구성원도 포함됩니다.</small>
      </fieldset>;
    })}
    <label className="span-2 checkbox-field"><input type="checkbox" checked={commentsEnabled} onChange={(event) => setCommentsEnabled(event.target.checked)} /><span>댓글 사용</span></label>
    <footer className="span-2"><button type="button" onClick={onCancel}>취소</button><button type="submit" className="primary" disabled={busy || invalid}>저장</button></footer>
  </form>;
}

function SystemSettings({ session, settings, admin, onChanged }: { session: Session; settings: TenantSettings | null; admin: boolean; onChanged: (message: string) => Promise<void> }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const currentModules = settings?.modules ?? [];
  if (!settings) return <Loading layout="form" label="설정을 불러오는 중" />;

  async function saveModule(module: "PERFORMANCE" | "ATTENDANCE" | "BOARD" | "STATUS", enabled: boolean) {
    setBusy(true); setError("");
    try {
      const customerModules = (["PERFORMANCE", "ATTENDANCE", "BOARD", "STATUS"] as const)
        .filter((item) => item === module ? enabled : currentModules.includes(item));
      await api.updateTenantModules(customerModules);
      const label = { PERFORMANCE: "성과", ATTENDANCE: "출퇴근 관리", BOARD: "게시판", STATUS: "현황" }[module];
      await onChanged(enabled ? `${label} 기능을 사용합니다.` : `${label} 기능을 끕니다.`);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "기능 사용 여부를 저장하지 못했습니다."); }
    finally { setBusy(false); }
  }

  async function saveVerification(taskVerificationEnabled: boolean) {
    setBusy(true); setError("");
    try {
      await api.updateTaskVerification(taskVerificationEnabled);
      await onChanged(taskVerificationEnabled ? "업무 검증 단계를 사용합니다." : "업무 검증 단계를 생략합니다.");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "업무 완료 흐름을 저장하지 못했습니다."); }
    finally { setBusy(false); }
  }


  /**
   * 기본 묶음은 끌 수 없고, 선택 기능은 고객사가 직접 켜고 끈다.
   */
  const features: { name: string; note?: string; on: boolean; locked: boolean; unreleased?: boolean; toggle?: (next: boolean) => void }[] = [
    { name: "기본", note: "업무 · 프로젝트 · 연구노트 · MCP 연동", on: true, locked: true },
    {
      name: "성과", note: "완료한 업무를 모아 집계하고 평가에 반영합니다", locked: !admin,
      on: settings.modules.includes("PERFORMANCE"),
      toggle: admin ? (next) => void saveModule("PERFORMANCE", next) : undefined,
    },
    {
      name: "출퇴근 관리", note: "출퇴근 기록과 전사 근태 정책을 사용합니다", locked: !admin,
      on: settings.modules.includes("ATTENDANCE"),
      toggle: admin ? (next) => void saveModule("ATTENDANCE", next) : undefined,
    },
    {
      name: "게시판", note: "조직별 읽기·쓰기 범위와 댓글 정책으로 소식을 공유합니다", locked: !admin,
      on: settings.modules.includes("BOARD"), toggle: admin ? (next) => void saveModule("BOARD", next) : undefined,
    },
    {
      name: "현황", note: "업무 데이터를 조건별 지표와 칼럼으로 구성합니다", locked: !admin,
      on: settings.modules.includes("STATUS"), toggle: admin ? (next) => void saveModule("STATUS", next) : undefined,
    },
  ];

  return <>
    {error && <div className="global-error">{error}<button onClick={() => setError("")}>닫기</button></div>}

    <section className="section-card">
      <header><div><span className="mono-label">FEATURES</span><h2>기능 관리</h2></div></header>
      <p className="permission-note">기능을 꺼도 데이터는 지워지지 않습니다. 다시 켜면 그대로 있습니다.</p>
      <DataTable cols="minmax(240px, 1fr) 96px">
        <div className="data-row data-head"><span>기능</span><span>사용</span></div>
        {features.map((feature) => <div className="data-row" key={feature.name}>
          <span className="feature-cell">
            <b className={feature.unreleased ? "muted" : ""}>{feature.name}</b>
            {feature.unreleased && <em className="pill muted">미출시</em>}
            {feature.note && <small>{feature.note}</small>}
          </span>
          <span><Toggle
            on={feature.on}
            label={`${feature.name} 사용`}
            disabled={feature.locked || busy}
            onChange={(next) => feature.toggle?.(next)}
          /></span>
        </div>)}
      </DataTable>
    </section>

    <section className="section-card">
      <header><div><span className="mono-label">TASK WORKFLOW</span><h2>업무 완료 흐름</h2></div><span className="pill muted">새로 발행하는 업무부터 적용</span></header>
      <p className="permission-note">검증 단계를 사용하면 수행보고 승인 뒤 담당자가 검증보고를 작성하고, 매니저가 최종 판정과 품질평가를 진행합니다.</p>
      <DataTable cols="minmax(240px, 1fr) 96px">
        <div className="data-row data-head"><span>단계</span><span>사용</span></div>
        <div className="data-row">
          <span className="feature-cell"><b>별도 검증 단계</b><small>수행 판정과 완료 평가 사이에 담당자 검증을 추가합니다.</small></span>
          <span><Toggle on={settings.taskVerificationEnabled} label="별도 검증 단계 사용" disabled={!admin || busy} onChange={(next) => void saveVerification(next)} /></span>
        </div>
      </DataTable>
    </section>

    <section className="section-card">
      <header><div><span className="mono-label">NETWORK</span><h2>접속 IP 제한</h2></div><span className="pill muted">미출시</span></header>
      <p className="permission-note">허용된 IP 대역에서만 회사 시스템에 접근하도록 제한합니다.</p>
      <div className="spine">
        <SpineField label="[적용 대상]"><span className="pill muted">웹 로그인</span> <span className="pill muted">API · MCP</span></SpineField>
        <SpineField label="[허용 대역]"><textarea rows={3} disabled placeholder="203.0.113.0/24" /></SpineField>
      </div>
    </section>

    <section className="section-card">
      <header><div><span className="mono-label">ROLES</span><h2>역할과 권한</h2></div></header>
      <p className="permission-note">역할은 조직 관리자 · 매니저 · 구성원 셋으로 고정입니다. 매니저는 담당 팀과 하위 팀의 업무를 관리합니다. 프로젝트 권한은 팀 권한과 별개로 부여되며, 프로젝트 구성원은 소속 팀과 무관하게 해당 프로젝트의 업무를 열람합니다. 현재 로그인 역할: {session.roles.map(role => roleText[role]).join(", ")}</p>
      <DataTable cols="minmax(220px, 2fr) 120px 130px 130px">
        <div className="data-row data-head"><span>권한</span><span>구성원</span><span>부서 관리자</span><span>조직 관리자</span></div>
        {[
          ["본인 업무 열람·수행", "○", "○", "○"],
          ["소속 부서 업무 열람", "○", "○", "○"],
          ["타 부서 공개 업무 열람", "○", "○", "○"],
          ["비공개 업무 열람", "같은 부서·프로젝트", "같은 부서·프로젝트", "○"],
          ["지시 발행", "×", "○", "○"],
          ["판정(승인·보완요청)", "×", "○", "○"],
          ["품질평가 입력", "×", "○", "○"],
          ["양식·척도 편집", "×", "×", "○"],
          ["구성원 초대·역할 변경", "×", "×", "○"],
          ["로그 열람·내보내기", "×", "×", "○"],
        ].map((row) => <div className="data-row" key={row[0]}>
          <span>{row[0]}</span><span className="mono">{row[1]}</span><span className="mono">{row[2]}</span><span className="mono">{row[3]}</span>
        </div>)}
      </DataTable>
    </section>
  </>;
}

function PolicySettings({ settings, onChanged }: { settings: TenantSettings | null; onChanged: (message: string) => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (!settings) return <Loading layout="form" label="정책을 불러오는 중" />;

  async function save(allowPastTaskDates: boolean, researchNoteConversionRole: "ASSIGNEE" | "MANAGER", message: string, collaborationPublishPolicy = settings?.collaborationPublishPolicy) {
    setBusy(true); setError("");
    try { await api.updateTenantPolicies(allowPastTaskDates, researchNoteConversionRole, collaborationPublishPolicy); await onChanged(message); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "정책을 저장하지 못했습니다."); }
    finally { setBusy(false); }
  }

  return <section className="section-card">
    <header><div><span className="mono-label">POLICIES</span><h2>정책</h2></div></header>
    <p className="permission-note">업무 발행과 연구노트 전환 시 서버가 아래 기준을 확인합니다.</p>
    {error && <div className="global-error">{error}<button onClick={() => setError("")}>닫기</button></div>}
    <div className="perm-rows">
      <div className="perm-row">
        <span><b>구성원의 협업 요청 발행</b><small>매니저 승인 필요 시, 발신 팀의 매니저 한 명을 지정해 승인받은 뒤 상대 팀에 전달합니다.</small></span>
        <span className="perm-choice">
          {([["DISABLED", "불가"], ["MANAGER_APPROVAL", "매니저 승인 필요"], ["FREE", "자유"]] as const).map(([value, label]) =>
            <button key={value} type="button" className={settings.collaborationPublishPolicy === value ? "on" : ""} disabled={busy}
              onClick={() => void save(settings.allowPastTaskDates, settings.researchNoteConversionRole, "협업 발행 정책을 저장했습니다.", value)}>{label}</button>)}
        </span>
      </div>
      <div className="perm-row">
        <span><b>과거 날짜 업무</b><small>업무 발행일보다 이른 시작일을 지정할 수 있는지 결정합니다.</small></span>
        <span className="perm-choice">
          <button type="button" className={!settings.allowPastTaskDates ? "on" : ""} disabled={busy} onClick={() => void save(false, settings.researchNoteConversionRole, "과거 날짜 업무를 허용하지 않습니다.")}>허용 안 함</button>
          <button type="button" className={settings.allowPastTaskDates ? "on" : ""} disabled={busy} onClick={() => void save(true, settings.researchNoteConversionRole, "과거 날짜 업무를 허용합니다.")}>허용</button>
        </span>
      </div>
      <div className="perm-row">
        <span><b>연구노트 변환</b><small>완료 업무를 연구노트로 전환할 수 있는 최소 역할을 정합니다.</small></span>
        <span className="perm-choice">
          <button type="button" className={settings.researchNoteConversionRole === "ASSIGNEE" ? "on" : ""} disabled={busy} onClick={() => void save(settings.allowPastTaskDates, "ASSIGNEE", "담당자도 연구노트로 전환할 수 있습니다.")}>담당자 포함</button>
          <button type="button" className={settings.researchNoteConversionRole === "MANAGER" ? "on" : ""} disabled={busy} onClick={() => void save(settings.allowPastTaskDates, "MANAGER", "매니저 이상만 연구노트로 전환할 수 있습니다.")}>매니저 이상</button>
        </span>
      </div>
    </div>
  </section>;
}

/** 회사 정보 입력값. 서버 응답과 비교해 바뀐 게 있을 때만 저장 버튼이 열린다. */
function toProfile(settings: TenantSettings | null) {
  return { displayName: settings?.displayName || "", businessNumber: settings?.businessNumber || "" };
}

function OrganizationSettings({ session, permissions, admin, settings, departments, users, onChanged }: { session: Session; permissions: AdditionalPermission[]; admin: boolean; settings: TenantSettings | null; departments: Department[]; users: User[]; onChanged: (message: string) => Promise<void> }) {
  const [dialog, setDialog] = useState<"department" | "invite" | null>(null);
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);
  const [profile, setProfile] = useState(() => toProfile(settings));
  const [profileAttempted, setProfileAttempted] = useState(false);
  useEffect(() => { setProfile(toProfile(settings)); }, [settings]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [unlockTarget, setUnlockTarget] = useState<User | null>(null);
  const [statusTarget, setStatusTarget] = useState<{ user: User; status: "ACTIVE" | "INACTIVE" } | null>(null);
  const [departTarget, setDepartTarget] = useState<User | null>(null);
  const [movingDepartment, setMovingDepartment] = useState<Department | null>(null);
  const [deleteDepartmentTarget, setDeleteDepartmentTarget] = useState<Department | null>(null);
  const [inviteDepartmentId, setInviteDepartmentId] = useState<string | null>(null);
  const [memberDepartment, setMemberDepartment] = useState<Department | null>(null);
  const [leaderDepartment, setLeaderDepartment] = useState<Department | null>(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [teamFilter, setTeamFilter] = useState<string | null>(null);
  const [memberMenu, setMemberMenu] = useState<string | null>(null);
  const [roleTarget, setRoleTarget] = useState<User | null>(null);
  const canManageAccounts = admin || permissions.includes("USER_ACCOUNT_MANAGE");
  const canManageAccount = (user: User) => canManageAccounts && (admin || !user.roles.includes("OWNER"));

  /** 회사 정보는 DESIGN.md가 온보딩 화면으로 규정하지만 온보딩 전용 화면이 없어 조직 설정 맨 앞에 둔다. */
  const profileDirty = settings ? JSON.stringify(profile) !== JSON.stringify(toProfile(settings)) : false;
  const logoSrc = settings?.logoUploaded
    ? api.publicTenantLogoUrl(session, settings.logoVersion) || `${api.workspaceLogoUrl(session.workspaceDomain)}&v=${logoVersion}`
    : "";

  async function saveProfile() {
    setBusy(true); setError("");
    try { await api.updateTenantProfile(profile.displayName, profile.businessNumber || null); await onChanged("회사 정보를 변경했습니다."); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "회사 정보를 저장하지 못했습니다."); }
    finally { setBusy(false); }
  }
  async function uploadLogo(file: File) {
    setLogoBusy(true); setError("");
    try { await api.uploadTenantLogo(file); setLogoVersion(Date.now()); await onChanged("회사 로고를 올렸습니다."); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "로고를 올리지 못했습니다."); }
    finally { setLogoBusy(false); }
  }
  async function removeLogo() {
    setLogoBusy(true); setError("");
    try { await api.removeTenantLogo(); setLogoVersion(Date.now()); await onChanged("회사 로고를 제거했습니다."); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "로고를 제거하지 못했습니다."); }
    finally { setLogoBusy(false); }
  }

  async function createDepartment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); const form = new FormData(event.currentTarget);
    try { await api.createDepartment({ name: String(form.get("name")), parentId: String(form.get("parentId") || "") || null, sortOrder: Number(form.get("sortOrder") || 0) }); setDialog(null); await onChanged("부서를 만들었습니다."); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "부서를 만들지 못했습니다."); }
  }
  async function updateDepartment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); if (!movingDepartment) return; const form = new FormData(event.currentTarget);
    try {
      await api.moveDepartment(movingDepartment.id, String(form.get("parentId") || "") || null);
      setMovingDepartment(null); await onChanged("팀을 이동했습니다.");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "팀을 이동하지 못했습니다."); }
  }

  const knownDepartmentIds = new Set(departments.map((department) => department.id));
  const scopeIds = teamFilter && teamFilter !== UNASSIGNED
    ? new Set([teamFilter, ...departmentDescendants(teamFilter, departments)])
    : null;
  const teamLabel = teamFilter === null ? "전체"
    : teamFilter === UNASSIGNED ? "미분류"
    : departments.find((department) => department.id === teamFilter)?.name || "팀";
  const visibleUsers = users
    .filter((user) => {
      if (teamFilter === null) return true;
      if (teamFilter === UNASSIGNED) return user.status !== "DEPARTED"
        && !userDepartmentIds(user).some((id) => knownDepartmentIds.has(id));
      return userDepartmentIds(user).some((id) => scopeIds?.has(id));
    })
    .filter((user) => `${user.displayName} ${user.email} ${user.jobTitle || ""}`.toLowerCase().includes(query.trim().toLowerCase()));
  return <>
    {error && <div className="global-error">{error}<button onClick={() => setError("")}>닫기</button></div>}
    {settings && <section className="section-card">
      <header><div><span className="mono-label">WORKSPACE</span><h2>회사 정보</h2></div><span className={`pill ${settings.status === "ACTIVE" ? "" : "warn"}`}>{settings.status === "ACTIVE" ? "이용 중" : settings.status}</span></header>
      <p className="permission-note">로그인 화면과 사이드바에 쓰입니다. 조직 관리자만 바꿀 수 있습니다.</p>
      <div className="spine">
        <SpineField label="[회사명 · 필수]">
          {admin
            ? <input aria-label="회사명" aria-invalid={profileAttempted && !profile.displayName.trim()} value={profile.displayName} maxLength={120} onChange={(event) => setProfile({ ...profile, displayName: event.target.value })} />
            : settings.displayName}
        </SpineField>
        <SpineField label="[사업자등록번호]">
          {admin
            ? <input aria-label="사업자등록번호" value={profile.businessNumber} maxLength={32} placeholder="000-00-00000" onChange={(event) => setProfile({ ...profile, businessNumber: event.target.value })} />
            : settings.businessNumber || "미등록"}
        </SpineField>
        <SpineField label="[회사 로고]">
          <div className="logo-field">
            <span className="logo-preview">
              {logoSrc ? <img src={logoSrc} alt={`${settings.displayName} 로고`} /> : <em>{initials(settings.displayName)}</em>}
            </span>
            <div>
              {admin && <div className="logo-actions">
                <label className="file-button">{logoBusy ? "올리는 중…" : <><Upload size={14} /> {settings.logoUploaded ? "이미지 교체" : "이미지 올리기"}</>}
                  <input type="file" hidden accept="image/png,image/jpeg" aria-label="회사 로고 이미지"
                    onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; if (file) void uploadLogo(file); }} />
                </label>
                {settings.logoUploaded && <button type="button" className="danger-quiet" disabled={logoBusy} onClick={() => void removeLogo()}><Trash2 size={13} /> 제거</button>}
              </div>}
              <small>PNG · JPEG, 10MB 이하. 중앙을 기준으로 256×256 JPEG 이미지로 저장합니다.</small>
            </div>
          </div>
        </SpineField>
      </div>
      {admin && profileAttempted && <RequirementHint missing={profile.displayName.trim() ? [] : ["회사명"]} />}
      {admin && <div className="schema-footer">
        <button type="button" disabled={!profileDirty || busy} onClick={() => setProfile(toProfile(settings))}>되돌리기</button>
        <button type="button" className="primary" disabled={!profileDirty || busy} onClick={event => { setProfileAttempted(true); if (!profile.displayName.trim()) { focusInvalidField(event.currentTarget.closest("section")!); return; } void saveProfile(); }}>{busy ? "저장 중…" : "회사 정보 저장"}</button>
      </div>}
    </section>}
    <section className="section-card org-card">
      <header>
        <div><span className="mono-label">ORGANIZATION</span><h2>조직</h2></div>
        <div className="header-tools">
          <div className="search-field"><Search size={14} /><input aria-label="구성원 검색" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="이름·이메일 검색" /></div>
          {admin && <button className="secondary" onClick={() => { setError(""); setDialog("department"); }}><Plus size={14} /> 팀 추가</button>}
          {canManageAccounts && <button className="primary" onClick={() => { setError(""); setInviteDepartmentId(null); setDialog("invite"); }}><UserPlus size={14} /> 초대</button>}
        </div>
      </header>
      <div className="org-panes">
        <div className="org-teams">
          <span className="mono-label">팀</span>
          <DepartmentTree
            departments={departments} users={users} admin={admin}
            selectedId={teamFilter}
            onSelect={(id) => setTeamFilter(id)}
            onAddMember={(department) => { setError(""); setMemberDepartment(department); }}
            onAssignLeader={(department) => { setError(""); setLeaderDepartment(department); }}
            onMove={(department) => { setError(""); setMovingDepartment(department); }}
            onDelete={(department) => { setError(""); setDeleteDepartmentTarget(department); }}
          />
          <p className="permission-note">매니저가 없는 팀은 설정 필요 상태입니다. 구성원을 추가하고 매니저를 지정하면 업무에 사용할 수 있습니다.</p>
        </div>
        <div className="org-members">
          <span className="mono-label">{teamLabel} · 구성원</span>
          <DataTable cols="minmax(180px, 2fr) minmax(110px, 1fr) 110px 90px 40px">
            <div className="data-row data-head"><span>이름 · 이메일</span><span>팀</span><span>역할</span><span>상태</span><span /></div>
            {visibleUsers.map((user) => {
              const state = user.status === "ACTIVE" ? "활성" : user.status === "INVITED" ? "초대 중" : user.status === "LOCKED" ? "잠금" : user.status === "DEPARTED" ? "퇴사" : "비활성";
              const teamNames = userDepartmentIds(user).map((id) => departments.find((department) => department.id === id)?.name).filter(Boolean);
              return <div className={`data-row${canManageAccount(user) ? " pick-row" : ""}`} key={user.id}>
                <span className="title-cell">{canManageAccount(user)
                  ? <button type="button" onClick={() => setSelectedUser(user)}>{user.displayName}<small>{user.email}</small></button>
                  : <><b>{user.displayName}</b><small>{user.email}</small></>}</span>
                <span>{teamNames.join(", ") || "미분류"}</span>
                <span>{roleText[primaryRole(user)]}</span>
                <span className={`state-cell user-${user.status.toLowerCase()}`}><i />{state}</span>
                <span className="row-menu">{canManageAccount(user) && <>
                  <button type="button" aria-label={`${user.displayName} 동작`} aria-expanded={memberMenu === user.id}
                    onClick={() => setMemberMenu(memberMenu === user.id ? null : user.id)}><MoreHorizontal size={15} /></button>
                  {memberMenu === user.id && <span className="row-menu-list">
                    {admin && user.status !== "DEPARTED" && <button type="button" onClick={() => { setMemberMenu(null); setRoleTarget(user); }}>역할</button>}
                    <button type="button" onClick={() => { setMemberMenu(null); setSelectedUser(user); }}>계정 관리</button>
                    {user.status === "INVITED" && <button type="button" onClick={async () => {
                      setMemberMenu(null); setError("");
                      try { await api.resendInvitation(user.id); await onChanged("새 초대 메일을 발송 대기열에 등록했습니다. 이전 링크는 만료됩니다."); }
                      catch (reason) { setError(reason instanceof Error ? reason.message : "초대 메일을 다시 보내지 못했습니다."); }
                    }}>초대 메일 다시 보내기</button>}
                    {user.status === "ACTIVE" && <button type="button" onClick={() => { setMemberMenu(null); setStatusTarget({ user, status: "INACTIVE" }); }}>비활성화</button>}
                    {user.status === "INACTIVE" && <button type="button" onClick={() => { setMemberMenu(null); setStatusTarget({ user, status: "ACTIVE" }); }}>활성화</button>}
                    {user.status === "LOCKED" && <button type="button" onClick={() => { setMemberMenu(null); setUnlockTarget(user); }}>잠금 해제</button>}
                    {user.status !== "DEPARTED" && <button type="button" className="danger-quiet" onClick={() => { setMemberMenu(null); setDepartTarget(user); }}>퇴사 처리</button>}
                  </span>}
                </>}</span>
              </div>;
            })}
          </DataTable>
          {visibleUsers.length === 0 && <Empty
            title={query ? "검색 결과가 없습니다" : "이 팀에 구성원이 없습니다"}
            copy={query ? "이름이나 이메일을 다시 확인해 주세요." : "초대 메일에서 비밀번호를 설정하거나 기존 계정으로 로그인해 참여할 수 있습니다."} />}
          <p className="permission-note">마지막 Owner는 강등·비활성화할 수 없습니다. 초대받은 사람은 메일의 링크에서 직접 비밀번호를 설정하거나 기존 계정으로 로그인해 수락합니다.</p>
        </div>
      </div>
    </section>

    {dialog === "department" && <ModalFrame title="팀 추가" eyebrow="NEW DEPARTMENT" onClose={() => setDialog(null)}><form className="modal-form" onSubmit={createDepartment}>
      <label className="span-2">팀 이름<input name="name" required maxLength={120} autoFocus /></label>
      <label>상위 팀<select name="parentId"><option value="">최상위 팀</option><DepartmentOptions departments={departments} /></select><small>선택한 팀 아래에 새 팀이 생성됩니다.</small></label>
      <label>정렬 순서<input name="sortOrder" type="number" defaultValue="0" /></label>
      <footer className="span-2"><button type="button" onClick={() => setDialog(null)}>취소</button><button className="primary">팀 추가</button></footer>
    </form></ModalFrame>}
    {dialog === "invite" && <InviteMemberModal departments={departments} initialDepartmentId={inviteDepartmentId}
      onClose={() => { setDialog(null); setInviteDepartmentId(null); }}
      onCreated={async () => { setDialog(null); setInviteDepartmentId(null); await onChanged("초대 메일을 발송 대기열에 등록했습니다."); }} />}
    {movingDepartment && <ModalFrame title={`${movingDepartment.name} 이동`} eyebrow="MOVE TEAM" onClose={() => setMovingDepartment(null)}><form className="modal-form" onSubmit={updateDepartment}>
      <label className="span-2">상위 팀<select name="parentId" defaultValue={movingDepartment.parentId || ""}><option value="">최상위 팀</option><DepartmentOptions departments={departments} excluded={departmentDescendants(movingDepartment.id, departments)} /></select><small>선택한 팀 아래로 이동합니다. 최상위 팀을 선택하면 조직의 첫 단계로 이동합니다.</small></label>
      <footer className="span-2"><button type="button" onClick={() => setMovingDepartment(null)}>취소</button><button className="primary">이동</button></footer>
    </form></ModalFrame>}
    {deleteDepartmentTarget && <ConfirmDialog
      title={`${deleteDepartmentTarget.name} 팀을 삭제할까요?`}
      eyebrow="DELETE TEAM" confirmLabel="삭제" busy={busy}
      onClose={() => setDeleteDepartmentTarget(null)}
      onConfirm={async () => {
        const target = deleteDepartmentTarget;
        setBusy(true); setError("");
        try {
          await api.deleteDepartment(target.id);
          if (teamFilter === target.id) setTeamFilter(null);
          setDeleteDepartmentTarget(null);
          await onChanged("팀을 삭제했습니다.");
        } catch (reason) { setDeleteDepartmentTarget(null); setError(reason instanceof Error ? reason.message : "팀을 삭제하지 못했습니다."); }
        finally { setBusy(false); }
      }}
    >하위 팀이나 구성원·업무·프로젝트 등의 사용 기록이 있으면 삭제할 수 없습니다.</ConfirmDialog>}
    {roleTarget && <MemberRoleModal
      user={roleTarget} users={users} departments={departments}
      onClose={() => setRoleTarget(null)}
      onChanged={async (message) => { setRoleTarget(null); await onChanged(message); }}
    />}
    {leaderDepartment && <DepartmentLeaderModal
      department={leaderDepartment} users={users}
      onClose={() => setLeaderDepartment(null)}
      onChanged={async (message) => { setLeaderDepartment(null); await onChanged(message); }}
    />}
    {memberDepartment && <DepartmentMemberModal
      department={memberDepartment} users={users}
      onClose={() => setMemberDepartment(null)}
      onInvite={() => { setInviteDepartmentId(memberDepartment.id); setMemberDepartment(null); setDialog("invite"); }}
      onChanged={async (message) => { setMemberDepartment(null); await onChanged(message); }}
    />}
    {selectedUser && <UserAdminModal user={selectedUser} departments={departments} restricted={!admin} onClose={() => setSelectedUser(null)} onChanged={onChanged} />}
    {statusTarget && <ConfirmDialog
      title={`${statusTarget.user.displayName} 님의 계정을 ${statusTarget.status === "ACTIVE" ? "활성화" : "비활성화"}할까요?`}
      eyebrow={statusTarget.status === "ACTIVE" ? "ACTIVATE ACCOUNT" : "DEACTIVATE ACCOUNT"}
      confirmLabel={statusTarget.status === "ACTIVE" ? "활성화" : "비활성화"} busy={busy}
      onClose={() => setStatusTarget(null)}
      onConfirm={async () => {
        setBusy(true); setError("");
        const target = statusTarget;
        try {
          const memberships = userDepartmentMemberships(target.user).map((membership) => ({
            departmentId: membership.departmentId, role: membership.role, primary: membership.primary,
          }));
          await api.updateUser(target.user.id, {
            departmentId: memberships.find((membership) => membership.primary)?.departmentId || null,
            departmentMemberships: memberships,
            roles: target.user.roles,
            jobTitle: target.user.jobTitle || null,
            jobLevel: target.user.jobLevel || null,
            status: target.status,
          });
          setStatusTarget(null);
          await onChanged(target.status === "ACTIVE" ? "계정을 활성화했습니다." : "계정을 비활성화하고 로그인 세션을 종료했습니다.");
        } catch (reason) {
          setStatusTarget(null);
          setError(reason instanceof Error ? reason.message : "계정 상태를 변경하지 못했습니다.");
        }
        finally { setBusy(false); }
      }}
    >
      <p>{statusTarget.status === "ACTIVE" ? "활성화하면 다시 로그인하고 업무를 수행할 수 있습니다." : "비활성화하면 로그인할 수 없고 현재 로그인 세션도 모두 종료됩니다."}</p>
    </ConfirmDialog>}
    {unlockTarget && <ConfirmDialog
      title={`${unlockTarget.displayName} 님의 잠금을 해제할까요?`}
      eyebrow="UNLOCK ACCOUNT" confirmLabel="잠금 해제" busy={busy}
      onClose={() => setUnlockTarget(null)}
      onConfirm={async () => {
        setBusy(true);
        try {
          await api.unlockUser(unlockTarget.id);
          setUnlockTarget(null);
          await onChanged("계정 잠금을 해제했습니다.");
        } finally { setBusy(false); }
      }}
    >
      <p>비밀번호를 여러 번 틀려 잠긴 계정입니다. 해제하면 바로 다시 로그인할 수 있고, 실패 횟수도 함께 초기화됩니다.</p>
      <p className="muted">잠금은 시간이 지나도 저절로 풀리지 않습니다. 본인이 비밀번호를 기억하지 못한다면 해제 대신 계정 관리에서 초기화하세요.</p>
    </ConfirmDialog>}
    {departTarget && <ConfirmDialog
      title={`${departTarget.displayName} 님을 퇴사 처리할까요?`}
      eyebrow="DEPART MEMBER" confirmLabel="퇴사 처리" busy={busy}
      onClose={() => setDepartTarget(null)}
      onConfirm={async () => {
        setBusy(true);
        try {
          await api.departUser(departTarget.id);
          setDepartTarget(null);
          await onChanged("퇴사 처리했습니다. 기존 이름과 이메일은 기록용으로 보존됩니다.");
        } finally { setBusy(false); }
      }}
    >
      <p>로그인과 모든 세션이 즉시 차단되고 활성 팀 소속이 종료됩니다.</p>
      <p className="muted">기존 업무·연구노트의 작성자 연결은 유지됩니다. 원래 이름과 이메일은 당시 기록을 식별하기 위한 아카이브로 보존되며, 같은 이메일로 새 계정을 만들 수 있습니다.</p>
    </ConfirmDialog>}
  </>;
}

const additionalPermissionOptions: Array<{ id: AdditionalPermission; label: string; description: string }> = [
  { id: "PROJECT_BUDGET_VIEW", label: "예산 조회", description: "권한이 있는 프로젝트 범위와 관계없이 전체 프로젝트 예산을 조회합니다." },
  { id: "PROJECT_BUDGET_EDIT", label: "예산 수정", description: "전체 프로젝트의 예산과 누적 집행액을 수정합니다. 예산 조회도 함께 부여됩니다." },
  { id: "RESEARCH_NOTE_VIEW_ALL", label: "전체 연구노트 조회", description: "작성자와 프로젝트 범위에 관계없이 조직의 모든 연구노트를 조회합니다." },
  { id: "USER_ACCOUNT_MANAGE", label: "사용자 계정 관리", description: "구성원 초대, 상태 변경, 잠금 해제, 퇴사 처리와 세션 종료를 수행합니다." },
  { id: "BOARD_MANAGE", label: "게시판 관리", description: "게시판의 읽기·쓰기 범위와 댓글 정책을 설정하고 모든 게시글을 관리합니다." },
  { id: "ATTENDANCE_MANAGE", label: "근태·휴가 관리", description: "전사 근태 현황, 근무 수정 요청, 휴가 종류와 신청·잔여량을 관리합니다." },
  { id: "COMPANY_RULE_MANAGE", label: "사규 관리", description: "사규 항목과 관련 문서, 담당자 및 양식을 등록·수정·삭제합니다." },
  { id: "WORK_GUIDE_MANAGE", label: "업무 도우미 관리", description: "업무 가이드 분류·문서·첨부자료를 관리하고 구성원 및 AI에 대한 공개 여부를 설정합니다." },
];

function AdditionalPermissionSettings({ users, onChanged }: { users: User[]; onChanged: (message: string) => Promise<void> }) {
  const [grants, setGrants] = useState<AdditionalPermissionGrant[] | null>(null);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<AdditionalPermission>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const candidates = users.filter((user) => user.status !== "DEPARTED" && !user.roles.includes("OWNER"));
  const target = candidates.find((user) => user.id === targetId);
  const editingExistingGrant = grants?.some((grant) => grant.userId === targetId) ?? false;

  const load = useCallback(async () => {
    setError("");
    try {
      const next = await api.additionalPermissionGrants();
      setGrants(Array.isArray(next) ? next : []);
    }
    catch (reason) { setError(reason instanceof Error ? reason.message : "권한을 불러오지 못했습니다."); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  function open(userId?: string) {
    if (!userId && candidates.length === 0) return;
    const current = grants?.find((grant) => grant.userId === userId)?.permissions || [];
    setTargetId(userId || ""); setSelected(new Set(current)); setError("");
  }

  function changeTarget(userId: string) {
    setTargetId(userId);
    setSelected(new Set(grants?.find((grant) => grant.userId === userId)?.permissions || []));
  }

  function toggle(permission: AdditionalPermission, checked: boolean) {
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(permission); else next.delete(permission);
      if (permission === "PROJECT_BUDGET_EDIT" && checked) next.add("PROJECT_BUDGET_VIEW");
      if (permission === "PROJECT_BUDGET_VIEW" && !checked) next.delete("PROJECT_BUDGET_EDIT");
      return next;
    });
  }

  async function save() {
    if (!targetId) return;
    setBusy(true); setError("");
    try {
      await api.replaceAdditionalPermissions(targetId, [...selected]);
      setTargetId(null);
      await load();
      await onChanged(selected.size ? "권한을 저장했습니다." : "권한을 회수했습니다.");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "권한을 저장하지 못했습니다."); }
    finally { setBusy(false); }
  }

  return <section className="section-card">
    <header>
      <div><span className="mono-label">PERMISSIONS</span><h2>권한</h2></div>
      <button type="button" className="primary" disabled={candidates.length === 0} onClick={() => open()}><Plus size={14} /> 권한 부여</button>
    </header>
    <p className="permission-note">조직 역할은 그대로 유지한 채 필요한 운영 기능을 개인에게 부여합니다. OWNER는 별도 등록 없이 모든 권한을 가집니다.</p>
    {error && !targetId && <div className="global-error">{error}</div>}
    {!grants ? <Loading layout="table" label="권한을 불러오는 중" /> : grants.length === 0
      ? <Empty title="권한을 부여한 구성원이 없습니다" copy="예산, 연구노트, 계정 또는 게시판 관리가 필요한 구성원에게 권한을 부여하세요." />
      : <DataTable cols="minmax(200px, 1.2fr) minmax(320px, 2fr) 80px">
        <div className="data-row data-head"><span>구성원</span><span>부여된 권한</span><span /></div>
        {grants.map((grant) => <div className="data-row" key={grant.userId}>
          <span className="title-cell"><b>{grant.displayName}</b><small>{grant.email}</small></span>
          <span>{grant.permissions.map((permission) => <span className="pill" key={permission}>{additionalPermissionOptions.find((option) => option.id === permission)?.label || permission}</span>)}</span>
          <span className="row-actions"><button type="button" onClick={() => open(grant.userId)}><Pencil size={12} /> 수정</button></span>
        </div>)}
      </DataTable>}

    {targetId !== null && <ModalFrame title="권한 설정" eyebrow="PERMISSIONS" onClose={() => { if (!busy) setTargetId(null); }}>
      <div className="modal-form">
        <div className="span-2"><UserPicker label="구성원" searchLabel="권한 구성원 검색" users={candidates} value={targetId ? [targetId] : []} onChange={ids => changeTarget(ids[0] || "")} /></div>
        <div className="role-options span-2">
          {additionalPermissionOptions.map((option) => <label className={selected.has(option.id) ? "on" : ""} key={option.id}>
            <input type="checkbox" disabled={!targetId} checked={selected.has(option.id)} onChange={(event) => toggle(option.id, event.target.checked)} />
            <span><b>{option.label}</b><small>{option.description}</small></span>
          </label>)}
        </div>
        {error && <div className="error-box span-2">{error}</div>}
        <footer className="span-2"><button type="button" onClick={() => setTargetId(null)}>취소</button><button type="button" className="primary" disabled={busy || !targetId || (!editingExistingGrant && selected.size === 0)} onClick={() => void save()}>{busy ? "저장 중…" : editingExistingGrant ? "권한 저장" : "권한 부여"}</button></footer>
      </div>
    </ModalFrame>}
  </section>;
}

function DepartmentMembershipPicker({ departments, value, onChange }: {
  departments: Department[];
  value: DepartmentMembershipCommand[];
  onChange: (memberships: DepartmentMembershipCommand[]) => void;
}) {
  const [emptyRows, setEmptyRows] = useState<string[]>([]);
  const primary = value.find((membership) => membership.primary) || value[0];
  const extras = value.filter((membership) => membership.departmentId !== primary?.departmentId);
  const selectedIds = new Set(value.map((membership) => membership.departmentId));
  // 새 팀은 매니저가 없어 실질 상태가 INACTIVE여도 구성원을 먼저 배치할 수 있어야 한다.
  const visible = departments.filter((department) => departmentConfiguredActive(department) || selectedIds.has(department.id));
  const availableCount = visible.filter((department) => !selectedIds.has(department.id)).length;

  function setPrimary(departmentId: string) {
    if (!departmentId) {
      onChange([]);
      return;
    }
    const nextPrimary = value.find((membership) => membership.departmentId === departmentId)
      || { departmentId, role: "MEMBER" as const, primary: true };
    onChange([
      { ...nextPrimary, primary: true },
      ...value.filter((membership) => membership.departmentId !== departmentId && membership.departmentId !== primary?.departmentId)
        .map((membership) => ({ ...membership, primary: false })),
    ]);
  }

  function replaceExtra(currentId: string, departmentId: string) {
    if (!departmentId || departmentId === currentId) return;
    onChange(value.map((membership) => membership.departmentId === currentId
      ? { ...membership, departmentId, primary: false }
      : membership));
  }

  function remove(departmentId: string) {
    onChange(value.filter((membership) => membership.departmentId !== departmentId));
  }

  return <fieldset className="membership-picker membership-editor span-2">
    <legend>팀</legend>
    <div className="membership-row membership-primary-row">
      <label htmlFor="member-primary-department">기본 팀</label>
      <select id="member-primary-department" aria-label="기본 팀" value={primary?.departmentId || ""} onChange={(event) => setPrimary(event.target.value)}>
        <option value="">팀 선택</option>
        {visible.map((department) => <option value={department.id} key={department.id}>{department.name}{!departmentConfiguredActive(department) ? " · 비활성" : department.status !== "ACTIVE" ? " · 설정 필요" : ""}</option>)}
      </select>
      <span aria-hidden="true" />
    </div>
    <div className="membership-extra-list" aria-label="추가 팀">
      {extras.map((membership, index) => <div className="membership-row" key={membership.departmentId}>
        <label htmlFor={`member-extra-department-${index}`}>추가 팀 {index + 1}</label>
        <select id={`member-extra-department-${index}`} aria-label={`추가 팀 ${index + 1}`} value={membership.departmentId} onChange={(event) => replaceExtra(membership.departmentId, event.target.value)}>
          {visible.filter((department) => department.id === membership.departmentId || !selectedIds.has(department.id))
            .map((department) => <option value={department.id} key={department.id}>{department.name}{!departmentConfiguredActive(department) ? " · 비활성" : department.status !== "ACTIVE" ? " · 설정 필요" : ""}</option>)}
        </select>
        <button type="button" className="danger-quiet" aria-label={`${departments.find((department) => department.id === membership.departmentId)?.name || "추가 팀"} 삭제`} onClick={() => remove(membership.departmentId)}><Trash2 size={13} /> 삭제</button>
      </div>)}
      {emptyRows.map((rowId, index) => <div className="membership-row" key={rowId}>
        <label htmlFor={`member-new-extra-department-${rowId}`}>추가 팀 {extras.length + index + 1}</label>
        <select id={`member-new-extra-department-${rowId}`} aria-label={`새 추가 팀 ${extras.length + index + 1}`} value="" onChange={(event) => {
          if (!event.target.value) return;
          onChange([...value, { departmentId: event.target.value, role: "MEMBER", primary: value.length === 0 }]);
          setEmptyRows((rows) => rows.filter((id) => id !== rowId));
        }}>
          <option value="">팀 선택</option>
          {visible.filter((department) => !selectedIds.has(department.id)).map((department) => <option value={department.id} key={department.id}>{department.name}{!departmentConfiguredActive(department) ? " · 비활성" : department.status !== "ACTIVE" ? " · 설정 필요" : ""}</option>)}
        </select>
        <button type="button" className="danger-quiet" aria-label="빈 추가 팀 삭제" onClick={() => setEmptyRows((rows) => rows.filter((id) => id !== rowId))}><Trash2 size={13} /> 삭제</button>
      </div>)}
    </div>
    <button type="button" className="membership-add" disabled={!primary || availableCount <= emptyRows.length} onClick={() => setEmptyRows((rows) => [...rows, crypto.randomUUID()])}><Plus size={13} /> 추가</button>
    <small>기본 팀은 업무 작성 시 기본값으로 사용됩니다. 필요한 경우 여러 팀을 추가할 수 있습니다.</small>
  </fieldset>;
}

function InviteMemberModal({ departments, initialDepartmentId, onClose, onCreated }: {
  departments: Department[];
  initialDepartmentId?: string | null;
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [memberships, setMemberships] = useState<DepartmentMembershipCommand[]>(() => initialDepartmentId
    ? [{ departmentId: initialDepartmentId, role: "MEMBER", primary: true }]
    : []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const primaryDepartmentId = memberships.find((membership) => membership.primary)?.departmentId || null;
    setBusy(true); setError("");
    try {
      await api.inviteUser({
        email: form.get("email"), displayName: form.get("displayName"),
        departmentId: primaryDepartmentId, departmentMemberships: memberships, roles: ["MEMBER"],
        employeeNumber: form.get("employeeNumber") || null,
      });
      await onCreated();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "사용자를 초대하지 못했습니다."); }
    finally { setBusy(false); }
  }

  return <ModalFrame title="구성원 초대" eyebrow="INVITE MEMBER" onClose={onClose}><form className="modal-form" onSubmit={submit}>
    <label>이름<input name="displayName" required maxLength={120} /></label>
    <label>이메일<input name="email" type="email" required /></label>
    <p className="permission-note span-2">초대 메일의 링크는 7일 동안 유효합니다. 새 계정은 직접 비밀번호를 설정하고, 기존 계정은 사용 중인 비밀번호로 로그인해 수락합니다.</p>
    <DepartmentMembershipPicker departments={departments} value={memberships} onChange={setMemberships} />
    <label>사번<input name="employeeNumber" /></label>
    <p className="permission-note span-2">구성원으로 초대됩니다. 매니저는 초대 후 구성원 목록의 ··· → 역할에서 지정합니다.</p>
    {error && <div className="error-box span-2">{error}</div>}
    <footer className="span-2"><button type="button" onClick={onClose}>취소</button><button className="primary" disabled={busy}>{busy ? "등록 중…" : "초대 메일 보내기"}</button></footer>
  </form></ModalFrame>;
}

function DepartmentMemberModal({ department, users, onClose, onInvite, onChanged }: {
  department: Department; users: User[];
  onClose: () => void; onInvite: () => void; onChanged: (message: string) => Promise<void>;
}) {
  const candidates = users.filter((user) => user.status !== "DEPARTED" && !userBelongsTo(user, department.id));
  const [userId, setUserId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    const user = candidates.find((candidate) => candidate.id === userId);
    if (!user) return;
    const current = userDepartmentMemberships(user);
    const memberships: DepartmentMembershipCommand[] = [
      ...current.map((membership) => ({
        departmentId: membership.departmentId, role: membership.role, primary: membership.primary,
      })),
      { departmentId: department.id, role: "MEMBER", primary: current.length === 0 },
    ];
    setBusy(true); setError("");
    try {
      await api.updateUser(user.id, {
        departmentId: memberships.find((membership) => membership.primary)?.departmentId || null,
        departmentMemberships: memberships,
        roles: user.roles,
        jobTitle: user.jobTitle || null,
        jobLevel: user.jobLevel || null,
        status: user.status,
      });
      await onChanged(`${department.name}에 구성원을 추가했습니다 — ${user.displayName}`);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "구성원을 추가하지 못했습니다."); }
    finally { setBusy(false); }
  }

  return <ModalFrame title={`${department.name} 구성원 추가`} eyebrow="ADD MEMBER" onClose={onClose}><div className="modal-form">
    {candidates.length ? <div className="span-2">
      <UserPicker label="구성원" searchLabel="추가할 구성원" users={candidates} value={userId ? [userId] : []} onChange={ids => setUserId(ids[0] || "")} />
      <small>기존 역할과 다른 팀 소속은 유지됩니다.</small>
    </div> : <div className="span-2"><Empty title="추가할 기존 구성원이 없습니다" copy="새 계정을 초대하면서 이 조직을 기본 소속으로 지정할 수 있습니다." /></div>}
    {error && <div className="error-box span-2">{error}</div>}
    <footer className="span-2">
      <button type="button" onClick={onClose}>취소</button>
      <button type="button" onClick={onInvite}>새 계정 초대</button>
      {candidates.length > 0 && <button type="button" className="primary" disabled={busy || !userId} onClick={() => void save()}>{busy ? "추가 중…" : "구성원 추가"}</button>}
    </footer>
  </div></ModalFrame>;
}

/** 기본 배정 관리자 지정은 기존 관리자 권한을 유지하면서 새 관리자를 추가한다. */
function DepartmentLeaderModal({ department, users, onClose, onChanged }: {
  department: Department; users: User[];
  onClose: () => void; onChanged: (message: string) => Promise<void>;
}) {
  const candidates = users.filter((user) => user.status === "ACTIVE");
  const current = candidates.find((user) => user.id === department.defaultManagerId && (userManagesDepartment(user, department.id) || user.roles.includes("OWNER")))
    || candidates.find((user) => userManagesDepartment(user, department.id))
    || candidates.find((user) => user.roles.includes("OWNER") && userBelongsTo(user, department.id));
  const [leaderId, setLeaderId] = useState(current?.id || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    if (!leaderId || leaderId === department.defaultManagerId) return;
    setBusy(true); setError("");
    try {
      await api.assignDepartmentLeader(department.id, leaderId);
      await onChanged(`기본 배정 관리자를 지정했습니다 — ${department.name}`);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "매니저를 지정하지 못했습니다."); }
    finally { setBusy(false); }
  }

  return <ModalFrame title={`${department.name} 매니저`} eyebrow="MANAGER" onClose={onClose}><div className="modal-form">
    <p className="span-2">현재 관리자: {candidates.filter(user => userManagesDepartment(user, department.id)).map(user => user.displayName).join(", ") || "미지정"}</p>
    {candidates.length ? <>
      <div className="span-2">
        <UserPicker label="기본 배정 매니저" searchLabel="매니저" users={candidates} value={leaderId ? [leaderId] : []} onChange={ids => setLeaderId(ids[0] || "")} />
        <small>기존 관리자는 유지됩니다. 선택한 사람은 팀 관리자로 추가되고 자동 일감 배정의 기본 대상이 됩니다. 관리자 추가·해제는 구성원의 역할에서도 변경할 수 있습니다.</small>
      </div>
      {!departmentConfiguredActive(department) && <div className="policy-note span-2">매니저를 지정해도 수동으로 비활성화한 팀은 자동 활성화되지 않습니다.</div>}
    </> : <div className="span-2"><Empty title="지정할 구성원이 없습니다" copy="먼저 활성 상태의 구성원을 초대해 주세요." /></div>}
    {error && <div className="error-box span-2">{error}</div>}
    <footer className="span-2">
      <button type="button" onClick={onClose}>취소</button>
      {candidates.length > 0 && <button type="button" className="primary" disabled={busy || !leaderId || leaderId === department.defaultManagerId} onClick={() => void save()}>{busy ? "지정 중…" : "기본 관리자 지정"}</button>}
    </footer>
  </div></ModalFrame>;
}

/**
 * 구성원의 역할과 맡을 팀을 지정한다. 기존의 다른 관리 팀은 유지한다.
 */
function MemberRoleModal({ user, departments, onClose, onChanged }: {
  user: User; users: User[]; departments: Department[];
  onClose: () => void; onChanged: (message: string) => Promise<void>;
}) {
  const current = primaryRole(user);
  const [attempted, setAttempted] = useState(false);
  const [role, setRole] = useState<Role>(current);
  const [departmentId, setDepartmentId] = useState(
    userDepartmentMemberships(user).find((membership) => membership.role === "MANAGER")?.departmentId || user.departmentId || "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const teamName = departments.find((department) => department.id === departmentId)?.name;

  async function submit(root: ParentNode) {
    setAttempted(true);
    if (role === "MANAGER" && !departmentId) { focusInvalidField(root); return; }
    setBusy(true); setError("");
    try {
      let memberships: DepartmentMembershipCommand[] = userDepartmentMemberships(user).map((membership) => ({
        departmentId: membership.departmentId,
        role: role === "MANAGER" ? membership.role : "MEMBER",
        primary: membership.primary,
      }));
      if (role === "MANAGER") {
        if (!memberships.some((membership) => membership.departmentId === departmentId)) {
          memberships.push({ departmentId, role: "MEMBER", primary: memberships.length === 0 });
        }
        memberships = memberships.map((membership) => ({
          ...membership,
          role: membership.departmentId === departmentId ? "MANAGER" : membership.role,
          primary: membership.departmentId === departmentId,
        }));
      }
      await api.updateUser(user.id, {
        departmentId: memberships.find((membership) => membership.primary)?.departmentId || null,
        departmentMemberships: memberships,
        roles: [role],
        // 역할만 바꾼다. 초대 중이던 구성원이 활성으로 바뀌면 안 된다.
        status: user.status,
      });
      await onChanged(role === "MANAGER" ? `매니저로 지정했습니다 — ${teamName || "팀"}` : `역할을 바꿨습니다 — ${roleText[role]}`);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "역할을 바꾸지 못했습니다."); }
    finally { setBusy(false); }
  }

  return <ModalFrame title={`${user.displayName} 역할`} eyebrow="MEMBER ROLE" onClose={onClose}><div className="modal-form">
    <div className="role-options span-2">
      {(["MEMBER", "MANAGER", "OWNER"] as Role[]).map((option) => <label key={option} className={role === option ? "on" : ""}>
        <input type="radio" name="member-role" value={option} checked={role === option} onChange={() => setRole(option)} />
        <span>
          <b>{roleText[option]}</b>
          <small>{option === "MEMBER" ? "배정된 업무 수행, 소속 팀 보드 조회"
            : option === "MANAGER" ? "맡은 팀과 하위 팀의 지시·판정. 한 팀에 여러 명 지정 가능"
            : "계약·결제·전체 설정. 조직 전체 범위"}</small>
        </span>
      </label>)}
    </div>

    {role === "MANAGER" && <label className="span-2"><span>맡을 팀 <em className="required-mark">필수</em></span>
      <select aria-label="맡을 팀" aria-invalid={attempted && !departmentId} value={departmentId} onChange={(event) => setDepartmentId(event.target.value)} required>
        <option value="">팀 선택</option><DepartmentOptions departments={departments} includeInactive />
      </select>
      <small>이 팀과 하위 팀의 업무를 보고 판정합니다.</small>
    </label>}

    {attempted && role === "MANAGER" && <RequirementHint className="span-2" missing={departmentId ? [] : ["맡을 팀"]} />}

    {role !== current && role === "MEMBER" && current === "MANAGER" && <div className="policy-note span-2">이 구성원의 관리 권한을 해제합니다. 마지막 활성 관리자가 해제되는 팀만 설정 필요 상태가 됩니다. 기존 업무의 지시자는 자동 변경되지 않습니다.</div>}
    {error && <div className="error-box span-2">{error}</div>}
    <footer className="span-2">
      <button type="button" onClick={onClose}>취소</button>
      <button type="button" className="primary" disabled={busy || (role === current && (role !== "MANAGER" || userManagesDepartment(user, departmentId)))}
        onClick={event => void submit(event.currentTarget.closest(".modal")!)}>{busy ? "변경 중…" : "역할 변경"}</button>
    </footer>
  </div></ModalFrame>;
}

function UserAdminModal({ user, departments, restricted = false, onClose, onChanged }: { user: User; departments: Department[]; restricted?: boolean; onClose: () => void; onChanged: (message: string) => Promise<void> }) {
  const [error, setError] = useState("");
  const [memberships, setMemberships] = useState<DepartmentMembershipCommand[]>(() => userDepartmentMemberships(user).map((membership) => ({
    departmentId: membership.departmentId, role: membership.role, primary: membership.primary,
  })));
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try { await api.updateUser(user.id, {
      departmentId: memberships.find((membership) => membership.primary)?.departmentId || null,
      departmentMemberships: memberships,
      roles: [selectedRole],
      status: user.status,
    }); await onChanged("구성원 정보를 변경했습니다."); onClose(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "구성원을 변경하지 못했습니다."); }
  }
  const selectedRole = primaryRole(user);
  return <ModalFrame title={user.displayName} eyebrow="MEMBER ACCESS" onClose={onClose}><form className="modal-form" onSubmit={submit}>
    {user.status === "DEPARTED" ? <div className="policy-note span-2">
      <b>퇴사 처리된 계정</b><br />{user.departedAt ? `${koTime(user.departedAt)} 처리 · ` : ""}원래 이름과 이메일은 기록 식별용 아카이브로 보존되어 있습니다.
    </div> : <>
    {restricted && selectedRole === "MANAGER"
      ? <div className="policy-note span-2">매니저의 담당 팀과 조직 역할은 OWNER만 변경할 수 있습니다.</div>
      : <DepartmentMembershipPicker departments={departments} value={memberships} onChange={setMemberships} />}
    <p className="permission-note span-2">역할은 구성원 목록의 ··· → 역할에서 바꿉니다. 현재 역할: {roleText[selectedRole]}</p></>}
    {error && <div className="error-box span-2">{error}</div>}
    <footer className="span-2"><button type="button" onClick={onClose}>{user.status === "DEPARTED" ? "닫기" : "취소"}</button>{user.status !== "DEPARTED" && <button className="primary">변경 저장</button>}</footer>
  </form></ModalFrame>;
}

const ROLE_OPTIONS = ["", "정량 목표", "검증 기준", "구체적 검증 계획"];

type DraftField = TaskSchemaFieldCommand & { fieldId?: string; systemField: boolean };

export function TaskFormsPage({ session, onChanged }: { session: Session; onChanged: (message: string) => Promise<void> }) {
  return <>
    <PageHead title="업무 양식" meta="조직에서 공용으로 사용할 업무 작성 양식을 관리합니다." />
    <WorkSettings admin owner={session.roles.includes("OWNER")} taskFormsOnly listFirst onChanged={onChanged} />
  </>;
}

/** 업무 설정 — 테넌트 기본 양식과 필요에 따라 추가한 업무 양식을 함께 관리한다. */
function WorkSettings({ admin, owner = false, taskFormsOnly = false, hideTaskForms = false, listFirst = false, onChanged }: {
  admin: boolean; owner?: boolean; taskFormsOnly?: boolean; hideTaskForms?: boolean; listFirst?: boolean; onChanged: (message: string) => Promise<void>;
}) {
  const [schemas, setSchemas] = useState<TaskSchema[]>([]);
  const [schema, setSchema] = useState<TaskSchema | null>(null);
  const [defaultSchemaId, setDefaultSchemaId] = useState("");
  const [draft, setDraft] = useState<DraftField[]>([]);
  const [creatingForm, setCreatingForm] = useState(false);
  const [newFormName, setNewFormName] = useState("");
  const [createAttempted, setCreateAttempted] = useState(false);
  const [schemaAttempted, setSchemaAttempted] = useState(false);
  const [reportAttempted, setReportAttempted] = useState(false);
  const [editorOpen, setEditorOpen] = useState(!listFirst);
  const [report, setReport] = useState<ReportSchema | null>(null);
  const [reportDraft, setReportDraft] = useState<ReportSchemaStageCommand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reportError, setReportError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setReportError("");
    try {
      // 두 규칙을 따로 싣는다. 한쪽이 실패해도 다른 쪽은 보여야 한다.
      const [configuration, reportConfiguration] = await Promise.all([
        api.taskConfiguration(),
        taskFormsOnly ? Promise.resolve(null) : api.reportConfiguration().catch((reason: unknown) => {
          setReportError(reason instanceof Error ? reason.message : "결과 보고 스키마를 불러오지 못했습니다.");
          return null;
        }),
      ]);
      const tenantSchemas = configuration.schemas.filter((item) => item.scopeType === "TENANT");
      const tenant = tenantSchemas.find((item) => item.id === configuration.defaultSchemaId) || tenantSchemas[0] || null;
      setSchemas(tenantSchemas);
      setDefaultSchemaId(configuration.defaultSchemaId || tenant?.id || "");
      setSchema(tenant);
      setDraft(toDraft(tenant));
      const tenantReport = reportConfiguration
        ? reportConfiguration.schemas.find((item) => item.scopeType === "TENANT") || reportConfiguration.schemas[0] || null
        : null;
      setReport(tenantReport);
      setReportDraft(toReportDraft(tenantReport));
    } catch (reason) { setError(reason instanceof Error ? reason.message : "업무 설정을 불러오지 못했습니다."); }
    finally { setLoading(false); }
  }, [taskFormsOnly]);
  useEffect(() => { void load(); }, [load]);

  function toDraft(source: TaskSchema | null): DraftField[] {
    return [...(source?.currentVersion.fields || [])]
      .filter((field) => field.active)
      .sort((left, right) => left.sortOrder - right.sortOrder)
      .map((field) => ({
        key: field.key, label: field.label, inputType: field.inputType, required: field.required,
        sortOrder: field.sortOrder, helpText: field.helpText, role: field.role || null,
        fieldId: field.id, systemField: field.systemField,
      }));
  }

  const update = (index: number, patch: Partial<DraftField>) =>
    setDraft((current) => current.map((field, at) => at === index ? { ...field, ...patch } : field));
  const move = (index: number, by: number) => setDraft((current) => {
    const next = [...current];
    const target = index + by;
    if (target < 0 || target >= next.length) return current;
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });
  const addField = () => setDraft((current) => [...current, {
    key: `f_${crypto.randomUUID().replace(/-/g, "").slice(0, 8)}`,
    label: "", inputType: "LONG_TEXT", required: false, sortOrder: current.length, helpText: "", role: null,
    systemField: false,
  }]);

  function selectSchema(id: string) {
    const selected = schemas.find((item) => item.id === id) || null;
    setSchema(selected);
    setDraft(toDraft(selected));
    setError("");
  }

  const dirty = JSON.stringify(toDraft(schema).map(({ fieldId: _id, systemField: _system, ...rest }) => rest))
    !== JSON.stringify(draft.map(({ fieldId: _id, systemField: _system, ...rest }, index) => ({ ...rest, sortOrder: index })));
  const incomplete = draft.some((field) => !field.label.trim());


  function toReportDraft(source: ReportSchema | null): ReportSchemaStageCommand[] {
    return [...(source?.stages || [])]
      .filter((stage) => stage.active)
      .sort((left, right) => left.sortOrder - right.sortOrder)
      .map((stage, stageIndex) => ({
        name: stage.name, sortOrder: stageIndex,
        fields: [...stage.fields].filter((field) => field.active).sort((left, right) => left.sortOrder - right.sortOrder)
          .map((field, index) => ({
            key: field.key, label: field.label, kind: field.kind, inputType: field.inputType,
            required: field.required, sortOrder: index, helpText: field.helpText, maxScore: field.maxScore ?? null,
          })),
      }));
  }
  const newReportField = (): ReportSchemaFieldCommand => ({
    key: `f_${crypto.randomUUID().replace(/-/g, "").slice(0, 8)}`,
    label: "", kind: "INPUT", inputType: "LONG_TEXT", required: false, sortOrder: 0, helpText: "", maxScore: null,
  });
  const updateStage = (index: number, patch: Partial<ReportSchemaStageCommand>) =>
    setReportDraft((current) => current.map((stage, at) => at === index ? { ...stage, ...patch } : stage));
  const moveStage = (index: number, by: number) => setReportDraft((current) => {
    const next = [...current];
    const target = index + by;
    if (target < 0 || target >= next.length) return current;
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });
  const updateReportField = (stageIndex: number, index: number, patch: Partial<ReportSchemaFieldCommand>) =>
    setReportDraft((current) => current.map((stage, at) => at === stageIndex
      ? { ...stage, fields: stage.fields.map((field, position) => position === index ? { ...field, ...patch } : field) }
      : stage));
  const moveReportField = (stageIndex: number, index: number, by: number) => setReportDraft((current) => current.map((stage, at) => {
    if (at !== stageIndex) return stage;
    const fields = [...stage.fields];
    const target = index + by;
    if (target < 0 || target >= fields.length) return stage;
    [fields[index], fields[target]] = [fields[target], fields[index]];
    return { ...stage, fields };
  }));
  const addReportField = (stageIndex: number) => setReportDraft((current) => current.map((stage, at) =>
    at === stageIndex ? { ...stage, fields: [...stage.fields, { ...newReportField(), sortOrder: stage.fields.length }] } : stage));

  const reportDirty = JSON.stringify(toReportDraft(report)) !== JSON.stringify(normalizeReport(reportDraft));
  const reportIncomplete = reportDraft.some((stage) => !stage.name.trim() || stage.fields.some((field) => !field.label.trim()));

  function normalizeReport(stages: ReportSchemaStageCommand[]): ReportSchemaStageCommand[] {
    return stages.map((stage, stageIndex) => ({
      ...stage, sortOrder: stageIndex,
      fields: stage.fields.map((field, index) => ({ ...field, sortOrder: index })),
    }));
  }

  async function saveReport() {
    if (!report) return;
    setBusy(true); setError("");
    try {
      await api.replaceReportSchema(report.id, normalizeReport(reportDraft));
      await load();
      await onChanged("결과 보고 스키마를 저장했습니다.");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "결과 보고 스키마를 저장하지 못했습니다."); }
    finally { setBusy(false); }
  }

  async function save() {
    if (!schema) return;
    setBusy(true); setError("");
    try {
      const updated = await api.replaceTaskSchemaFields(schema.id, draft.map((field, index) => ({
        key: field.key, label: field.label.trim(), inputType: field.inputType, required: field.required,
        sortOrder: index, helpText: field.helpText.trim(), role: field.role || null,
      })));
      setSchema(updated); setDraft(toDraft(updated));
      setSchemas((current) => current.map((item) => item.id === updated.id ? updated : item));
      await onChanged(`${updated.name} 양식을 저장했습니다.`);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "업무 양식을 저장하지 못했습니다."); }
    finally { setBusy(false); }
  }

  async function createForm() {
    if (!schema || !newFormName.trim()) return;
    setBusy(true); setError("");
    try {
      const created = await api.createTaskSchema({
        name: newFormName.trim(), scopeType: "TENANT", scopeId: null, publish: true,
        fields: draft.map((field, index) => ({
          key: field.key, label: field.label.trim(), inputType: field.inputType, required: field.required,
          sortOrder: index, helpText: field.helpText.trim(), role: field.role || null,
        })),
      });
      setSchemas((current) => [...current, created]); setSchema(created); setDraft(toDraft(created));
      setNewFormName(""); setCreatingForm(false); setEditorOpen(true);
      await onChanged(`${created.name} 양식을 만들었습니다.`);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "업무 양식을 만들지 못했습니다."); }
    finally { setBusy(false); }
  }

  async function changeFormStatus(next: "ACTIVE" | "INACTIVE", target = schema) {
    if (!target) return;
    setBusy(true); setError("");
    try {
      await api.setTaskSchemaStatus(target.id, next);
      const updated = { ...target, status: next };
      if (schema?.id === updated.id) setSchema(updated);
      setSchemas((current) => current.map((item) => item.id === updated.id ? updated : item));
      await onChanged(next === "ACTIVE" ? `${target.name} 양식을 다시 사용합니다.` : `${target.name} 양식 사용을 중지했습니다.`);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "양식 상태를 바꾸지 못했습니다."); }
    finally { setBusy(false); }
  }

  function openEditor(target: TaskSchema) {
    selectSchema(target.id);
    setCreatingForm(false);
    setSchemaAttempted(false); setEditorOpen(true);
  }

  function startCreate(target: TaskSchema | null, duplicate: boolean) {
    const source = target || schemas.find((item) => item.id === defaultSchemaId) || schemas[0] || null;
    if (source) selectSchema(source.id);
    setNewFormName(duplicate && source ? `${source.name} 복사본` : "");
    setCreateAttempted(false); setCreatingForm(true);
    setEditorOpen(false);
  }

  if (loading) return <Loading layout="table" label="업무 설정을 불러오는 중" />;
  const removed = (schema?.currentVersion.fields || []).filter((field) => !field.active && !draft.some((item) => item.key === field.key));
  const missingRoles = ROLE_OPTIONS.slice(1, 3).filter((role) => !draft.some((field) => field.role === role));
  const canEditSchema = Boolean(admin && schema && (schema.id !== defaultSchemaId || owner));

  return <>
    {error && <div className="global-error">{error}<button onClick={() => setError("")}>닫기</button></div>}

    {!hideTaskForms && listFirst && !editorOpen && <section className="section-card task-form-list-card">
      <header>
        <div><span className="mono-label">TASK FORMS</span><h2>양식 목록</h2></div>
        <button type="button" className="primary" onClick={() => startCreate(null, false)}><Plus size={14} /> 양식 추가</button>
      </header>
      <p className="permission-note">전사 표준은 모든 업무의 기본값이며 조직 소유자만 수정할 수 있습니다. 매니저는 전사 표준을 복제하거나 별도 양식을 만들어 운영할 수 있습니다.</p>
      {creatingForm && <div className="task-form-create task-form-create-from-list">
        <div><b>새 업무 양식</b><span>기준 양식의 항목을 복사해 새 양식을 만듭니다.</span></div>
        <label><span>기준 양식</span><select aria-label="복제할 기준 양식" value={schema?.id || ""} onChange={(event) => selectSchema(event.target.value)}>
          {schemas.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}
        </select></label>
        <input aria-label="새 업무 양식 이름 (필수)" aria-invalid={createAttempted && !newFormName.trim()} value={newFormName} maxLength={120} autoFocus placeholder="양식 이름 · 필수" onChange={(event) => setNewFormName(event.target.value)} />
        {createAttempted && <RequirementHint missing={newFormName.trim() ? [] : ["양식 이름"]} />}
        <span className="task-form-create-actions"><button type="button" className="secondary" onClick={() => setCreatingForm(false)}>취소</button><button type="button" className="primary" disabled={busy} onClick={event => { setCreateAttempted(true); if (!newFormName.trim()) { focusInvalidField(event.currentTarget.closest(".task-form-create")!); return; } void createForm(); }}>{busy ? "만드는 중…" : "추가"}</button></span>
      </div>}
      <div className="task-form-list" role="table" aria-label="업무 양식 목록">
        <div className="task-form-list-head" role="row"><span>양식</span><span>항목</span><span>상태</span><span>최근 게시</span><span>관리</span></div>
        {schemas.map((item) => {
          const isDefault = item.id === defaultSchemaId;
          const editable = !isDefault || owner;
          return <div className="task-form-list-row" role="row" key={item.id}>
            <span className="task-form-list-name"><b>{item.name}</b><small>{isDefault ? "모든 업무에 우선 적용되는 전사 기본 양식" : "업무 생성 시 선택할 수 있는 추가 양식"}</small></span>
            <span>{item.currentVersion.fields.filter((field) => field.active).length}개</span>
            <span>{isDefault ? <em className="pill">전사 표준</em> : <Toggle on={item.status === "ACTIVE"} label={`${item.name} 사용`} disabled={busy} onChange={(next) => void changeFormStatus(next ? "ACTIVE" : "INACTIVE", item)} />}</span>
            <span>{item.currentVersion.publishedAt ? koTime(item.currentVersion.publishedAt) : "—"}</span>
            <span className="task-form-list-actions">
              <button type="button" className="secondary" aria-label={`${item.name} 복제`} onClick={() => startCreate(item, true)}><Copy size={13} /> 복제</button>
              <button type="button" className="secondary" aria-label={`${item.name} ${editable ? "편집" : "보기"}`} onClick={() => openEditor(item)}>{editable ? <Pencil size={13} /> : <Eye size={13} />}{editable ? "편집" : "보기"}</button>
            </span>
          </div>;
        })}
      </div>
    </section>}

    {!hideTaskForms && (!listFirst || editorOpen) && <section className="section-card">
      <header><div>{listFirst && <button type="button" className="task-form-back" onClick={() => { setEditorOpen(false); setCreatingForm(false); }}><ArrowLeft size={14} /> 양식 목록</button>}<span className="mono-label">TASK FORM</span><h2>{schema?.name || "업무 양식"}</h2></div><span className="pill muted">{schema?.id === defaultSchemaId ? "전사 표준" : "추가 양식"}</span></header>
      <p className="permission-note">{schema?.id === defaultSchemaId && !owner
        ? "전사 표준은 조직 소유자만 수정할 수 있습니다. 필요한 경우 이 양식을 복제해 별도 양식으로 사용하세요."
        : "항목을 고친 뒤에도 이미 발행된 과거 지시서의 값은 그대로 보관됩니다."}</p>
      {!listFirst && <div className="task-form-toolbar">
        <label><span>편집할 양식</span><select aria-label="편집할 업무 양식" value={schema?.id || ""} onChange={(event) => selectSchema(event.target.value)}>
          {schemas.map((item) => <option value={item.id} key={item.id}>{item.name}{item.id === defaultSchemaId ? " · 기본" : ""}{item.status === "INACTIVE" ? " · 사용 중지" : ""}</option>)}
        </select></label>
        {schema && <span className="task-form-state">
          {schema.id === defaultSchemaId ? <em className="pill">기본 양식</em> : <Toggle on={schema.status === "ACTIVE"} label={`${schema.name} 사용`} disabled={!admin || busy} onChange={(next) => void changeFormStatus(next ? "ACTIVE" : "INACTIVE")} />}
        </span>}
        {admin && <button type="button" onClick={() => setCreatingForm((current) => !current)}><Plus size={14} /> 새 양식</button>}
      </div>}
      {!listFirst && creatingForm && <div className="task-form-create">
        <div><b>현재 양식 복제</b><span>{schema?.name}의 항목을 복사한 뒤 자유롭게 수정할 수 있습니다.</span></div>
        <input aria-label="새 업무 양식 이름 (필수)" aria-invalid={createAttempted && !newFormName.trim()} value={newFormName} maxLength={120} autoFocus placeholder="양식 이름 · 필수" onChange={(event) => setNewFormName(event.target.value)} />
        {createAttempted && <RequirementHint missing={newFormName.trim() ? [] : ["양식 이름"]} />}
        <button type="button" disabled={busy} onClick={event => { setCreateAttempted(true); if (!newFormName.trim()) { focusInvalidField(event.currentTarget.closest(".task-form-create")!); return; } void createForm(); }}>만들기</button>
      </div>}
      <div className="schema-editor">
        <div className="schema-row schema-head">
          <span>항목</span><span>설명</span><span>입력 타입</span><span>역할</span><span>필수</span><span />
        </div>
        {draft.map((field, index) => <div className={`schema-row${field.systemField ? " system" : ""}`} key={field.key}>
          <span>
            <input className={schemaAttempted && !field.label.trim() ? "field-missing" : ""} aria-label={`항목 ${index + 1} 이름`} value={field.label} disabled={!canEditSchema} maxLength={120}
              placeholder="[항목 이름]" onChange={(event) => update(index, { label: event.target.value })} />
            {field.systemField && <em title="업무 보드·집계·추천이 이 값으로 돕니다">고정</em>}
          </span>
          <input aria-label={`항목 ${index + 1} 설명`} value={field.helpText} disabled={!canEditSchema} maxLength={2000}
            placeholder="작성자에게 보일 안내" onChange={(event) => update(index, { helpText: event.target.value })} />
          <select aria-label={`항목 ${index + 1} 입력 타입`} value={field.inputType} disabled={!canEditSchema || field.systemField}
            onChange={(event) => update(index, { inputType: event.target.value as DraftField["inputType"] })}>
            {(Object.keys(inputTypeText) as DraftField["inputType"][]).map((type) => <option value={type} key={type}>{inputTypeText[type]}</option>)}
          </select>
          <select aria-label={`항목 ${index + 1} 역할`} value={field.role || ""} disabled={!canEditSchema}
            onChange={(event) => update(index, { role: event.target.value || null })}>
            {ROLE_OPTIONS.map((role) => <option value={role} key={role || "none"}>{role || "—"}</option>)}
          </select>
          <span><Toggle on={field.required} label={`${field.label || "항목"} 필수`} disabled={!canEditSchema} onChange={(next) => update(index, { required: next })} /></span>
          <span className="schema-actions">
            <button type="button" aria-label={`항목 ${index + 1} 위로`} disabled={!canEditSchema || index === 0} onClick={() => move(index, -1)}>↑</button>
            <button type="button" aria-label={`항목 ${index + 1} 아래로`} disabled={!canEditSchema || index === draft.length - 1} onClick={() => move(index, 1)}>↓</button>
            <button type="button" aria-label={`항목 ${index + 1} 삭제`} className="danger-quiet"
              disabled={!canEditSchema || field.systemField} title={field.systemField ? "업무 자체가 성립하려면 있어야 하는 항목입니다" : "항목 삭제"}
              onClick={() => setDraft((current) => current.filter((_, at) => at !== index))}><Trash2 size={13} /></button>
          </span>
        </div>)}
        {canEditSchema && <button type="button" className="schema-add" onClick={addField}><Plus size={14} /> 항목 추가</button>}
      </div>
      {removed.length > 0 && <div className="schema-removed">
        <span className="mono-label">지운 항목</span>
        {removed.map((field) => <span key={field.key}>
          {field.label || field.key}
          {canEditSchema && <button type="button" onClick={() => setDraft((current) => [...current, {
            key: field.key, label: field.label, inputType: field.inputType, required: field.required,
            sortOrder: current.length, helpText: field.helpText, role: field.role || null, systemField: field.systemField,
          }])}>되살리기</button>}
        </span>)}
        <small>지운 항목의 과거 값은 그대로 보관됩니다. 되살리면 같은 항목으로 이어집니다.</small>
      </div>}
      {missingRoles.length > 0 && <p className="policy-note">역할이 비어 있습니다 — {missingRoles.join(" · ")}. 요청서에는 이 역할 항목이 각각 최소 하나 있어야 판정에서 근거를 짚을 수 있습니다.</p>}
      {canEditSchema && schemaAttempted && <RequirementHint missing={draft.flatMap((field, index) => field.label.trim() ? [] : [`항목 ${index + 1} 이름`])} />}
      {canEditSchema && <div className="schema-footer">
        <button type="button" disabled={!dirty || busy} onClick={() => setDraft(toDraft(schema))}>되돌리기</button>
        <button type="button" className="primary" disabled={!dirty || busy} onClick={event => { setSchemaAttempted(true); if (incomplete) { focusInvalidField(event.currentTarget.closest("section")!); return; } void save(); }}>{busy ? "저장 중…" : "양식 저장"}</button>
      </div>}
    </section>}

    {!taskFormsOnly && <><section className="section-card">
      <header><div><span className="mono-label">REPORT SCHEMA</span><h2>결과 보고 스키마</h2></div><span className="pill muted">전사 규칙</span></header>
      <p className="permission-note">담당자가 회차마다 제출할 항목입니다. 단계를 늘리면 착수·중간·완료 보고로 나눌 수 있습니다. <b>작성 항목</b>은 담당자가 채우고, <b>평가기준 항목</b>은 판정자가 채점합니다.</p>
      {report ? <>
        {reportDraft.map((stage, stageIndex) => <div className="span-section" key={stage.name || stageIndex}>
          <header>
            <span className="stage-name">
              <span className="mono-label">단계 {stageIndex + 1}</span>
              <input className={reportAttempted && !stage.name.trim() ? "field-missing" : ""} aria-label={`단계 ${stageIndex + 1} 이름`} value={stage.name} disabled={!admin} maxLength={120}
                onChange={(event) => updateStage(stageIndex, { name: event.target.value })} />
            </span>
            {admin && <span className="schema-actions">
              <button type="button" aria-label={`단계 ${stageIndex + 1} 위로`} disabled={stageIndex === 0} onClick={() => moveStage(stageIndex, -1)}>↑</button>
              <button type="button" aria-label={`단계 ${stageIndex + 1} 아래로`} disabled={stageIndex === reportDraft.length - 1} onClick={() => moveStage(stageIndex, 1)}>↓</button>
              <button type="button" aria-label={`단계 ${stageIndex + 1} 삭제`} className="danger-quiet" disabled={reportDraft.length === 1}
                title={reportDraft.length === 1 ? "단계는 최소 하나 필요합니다" : "단계 삭제"}
                onClick={() => setReportDraft((current) => current.filter((_, at) => at !== stageIndex))}><Trash2 size={13} /></button>
            </span>}
          </header>
          <div className="schema-editor">
            <div className="schema-row report-row schema-head">
              <span>항목</span><span>설명</span><span>종류</span><span>입력 타입</span><span>만점</span><span>필수</span><span />
            </div>
            {stage.fields.map((field, index) => <div className="schema-row report-row" key={field.key}>
              <input className={reportAttempted && !field.label.trim() ? "field-missing" : ""} aria-label={`단계 ${stageIndex + 1} 항목 ${index + 1} 이름`} value={field.label} disabled={!admin} maxLength={120}
                placeholder="항목 이름" onChange={(event) => updateReportField(stageIndex, index, { label: event.target.value })} />
              <input aria-label={`단계 ${stageIndex + 1} 항목 ${index + 1} 설명`} value={field.helpText} disabled={!admin} maxLength={2000}
                placeholder="작성자에게 보일 안내" onChange={(event) => updateReportField(stageIndex, index, { helpText: event.target.value })} />
              <select aria-label={`단계 ${stageIndex + 1} 항목 ${index + 1} 종류`} value={field.kind} disabled={!admin}
                onChange={(event) => {
                  const kind = event.target.value as ReportSchemaFieldCommand["kind"];
                  updateReportField(stageIndex, index, { kind, maxScore: kind === "CRITERION" ? field.maxScore || 5 : null, inputType: kind === "CRITERION" ? "NUMBER" : field.inputType });
                }}>
                <option value="INPUT">작성 항목</option><option value="CRITERION">평가기준 항목</option>
              </select>
              <select aria-label={`단계 ${stageIndex + 1} 항목 ${index + 1} 입력 타입`} value={field.inputType} disabled={!admin || field.kind === "CRITERION"}
                onChange={(event) => updateReportField(stageIndex, index, { inputType: event.target.value as ReportSchemaFieldCommand["inputType"] })}>
                {(["LONG_TEXT", "SHORT_TEXT", "NUMBER", "DATE_RANGE", "FILE"] as const).map((type) => <option value={type} key={type}>{inputTypeText[type]}</option>)}
              </select>
              <input aria-label={`단계 ${stageIndex + 1} 항목 ${index + 1} 만점`} type="number" min={1} max={100}
                value={field.maxScore ?? ""} disabled={!admin || field.kind !== "CRITERION"} placeholder="—"
                onChange={(event) => updateReportField(stageIndex, index, { maxScore: event.target.value ? Number(event.target.value) : null })} />
              <span><Toggle on={field.required} label={`${field.label || "항목"} 필수`} disabled={!admin} onChange={(next) => updateReportField(stageIndex, index, { required: next })} /></span>
              <span className="schema-actions">
                <button type="button" aria-label={`단계 ${stageIndex + 1} 항목 ${index + 1} 위로`} disabled={!admin || index === 0} onClick={() => moveReportField(stageIndex, index, -1)}>↑</button>
                <button type="button" aria-label={`단계 ${stageIndex + 1} 항목 ${index + 1} 아래로`} disabled={!admin || index === stage.fields.length - 1} onClick={() => moveReportField(stageIndex, index, 1)}>↓</button>
                <button type="button" aria-label={`단계 ${stageIndex + 1} 항목 ${index + 1} 삭제`} className="danger-quiet" disabled={!admin || stage.fields.length === 1}
                  onClick={() => setReportDraft((current) => current.map((item, at) => at === stageIndex ? { ...item, fields: item.fields.filter((_, position) => position !== index) } : item))}><Trash2 size={13} /></button>
              </span>
            </div>)}
            {admin && <button type="button" className="schema-add" onClick={() => addReportField(stageIndex)}><Plus size={14} /> 항목 추가</button>}
          </div>
        </div>)}
        {admin && <button type="button" className="schema-add" onClick={() => setReportDraft((current) => [...current, { name: `단계 ${current.length + 1}`, sortOrder: current.length, fields: [newReportField()] }])}><Plus size={14} /> 단계 추가</button>}
        <p className="permission-note">항목을 지우면 새 회차에서 사라지고 과거 제출물에는 그대로 남습니다. 평가기준 항목은 판정 화면의 채점 항목이 됩니다.</p>
        {admin && reportAttempted && <RequirementHint missing={reportDraft.flatMap((stage, stageIndex) => [
          ...(!stage.name.trim() ? [`단계 ${stageIndex + 1} 이름`] : []),
          ...stage.fields.flatMap((field, fieldIndex) => field.label.trim() ? [] : [`단계 ${stageIndex + 1} 항목 ${fieldIndex + 1} 이름`]),
        ])} />}
        {admin && <div className="schema-footer">
          <button type="button" disabled={!reportDirty || busy} onClick={() => setReportDraft(toReportDraft(report))}>되돌리기</button>
          <button type="button" className="primary" disabled={!reportDirty || busy} onClick={event => { setReportAttempted(true); if (reportIncomplete) { focusInvalidField(event.currentTarget.closest("section")!); return; } void saveReport(); }}>{busy ? "저장 중…" : "전사 규칙 저장"}</button>
        </div>}
      </> : <Empty
        title={reportError ? "결과 보고 스키마를 불러오지 못했습니다" : "결과 보고 스키마가 없습니다"}
        copy={reportError || "화면을 새로 고치면 전사 기본 규칙이 만들어집니다."} />}
    </section>

    <section className="section-card">
      <header><div><span className="mono-label">DIFFICULTY</span><h2>난이도 설정</h2></div><span className="pill muted">고정 3단계</span></header>
      <p className="permission-note">지시 발신자가 업무 등록 시 태깅합니다.</p>
      <DataTable cols="70px 120px minmax(240px, 3fr)">
        <div className="data-row data-head"><span>단계</span><span>이름</span><span>태깅 가이드</span></div>
        {[["1", "하"], ["2", "중"], ["3", "상"]].map((row) => <div className="data-row" key={row[0]}>
          <span className="mono">{row[0]}</span><span><b>{row[1]}</b></span><span className="muted">—</span>
        </div>)}
      </DataTable>
    </section>

    <section className="section-card">
      <header><div><span className="mono-label">DOCUMENT NUMBER</span><h2>문서번호 규칙</h2></div><span className="pill muted">미출시</span></header>
      <p className="permission-note">모든 문서에 고유번호가 부여되며 발행 후 변경할 수 없습니다. GMP 문서번호 체계와 같은 규칙을 씁니다.</p>
      <div className="docno">
        {["AF2", "REQ", "2026", "0412", "02"].map((part, index) => <span key={part}>{index > 0 && <i>-</i>}<em>{part}</em></span>)}
      </div>
      <DataTable cols="minmax(150px, 1.2fr) 120px minmax(220px, 2fr)">
        <div className="data-row data-head"><span>구성</span><span>예시</span><span>설명</span></div>
        {[["프로젝트 코드", "AF2", "프로젝트 없는 업무는 팀 코드"], ["문서 종류", "REQ / PRG / VRF", "요청서 · 진행 보고서 · 검증 보고서"], ["연도", "2026", "발행 연도"], ["업무 일련번호", "0412", "업무 생성 시 채번, 재사용 없음"], ["회차", "02", "요청서는 생략"]].map((row) => <div className="data-row" key={row[0]}>
          <span><b>{row[0]}</b></span><span className="mono">{row[1]}</span><span>{row[2]}</span>
        </div>)}
      </DataTable>
    </section></>}

  </>;
}

function AuditSettings({ users }: { users: User[] }) {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [source, setSource] = useState("ALL");
  const load = useCallback(async (cursor?: string) => {
    setLoading(true);
    try {
      const page = await api.auditEvents(100, cursor);
      setEvents((current) => cursor ? [...current, ...page.items] : page.items);
      setNextCursor(page.nextCursor || null);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "로그를 불러오지 못했습니다."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const actorName = (event: AuditEvent) => users.find((user) => user.id === event.actorId)?.displayName || event.actorId.slice(0, 8);
  const visible = events.filter((event) => (source === "ALL" || event.source === source)
    && `${auditActionText[event.action] || ""} ${event.action} ${event.resourceType} ${event.resourceId} ${actorName(event)}`.toLowerCase().includes(query.trim().toLowerCase()));
  /** 조회한 범위를 그대로 CSV로 내려준다. 서버 내보내기가 붙으면 그쪽으로 옮긴다. */
  function exportCsv() {
    const rows = [["시각", "수행자", "종류", "내용", "표면"], ...visible.map((event) => [koStamp(event.occurredAt), actorName(event), auditActionText[event.action] || event.action, `${event.resourceType}:${event.resourceId}`, event.source])];
    const csv = rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url; link.download = "angelflow-log.csv"; link.click();
    URL.revokeObjectURL(url);
  }

  return <>
    {error && <div className="global-error">{error}</div>}
    <section className="section-card">
      <header>
        <div><span className="mono-label">LOG</span><h2>로그 {visible.length}</h2></div>
        <div className="header-tools">
          <div className="search-field"><Search size={14} /><input aria-label="로그 검색" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="종류·내용 검색" /></div>
          <select aria-label="표면" value={source} onChange={(event) => setSource(event.target.value)}><option value="ALL">모든 표면</option><option value="WEB">WEB</option><option value="MCP">MCP</option><option value="BATCH">BATCH</option></select>
          <button className="secondary" disabled={visible.length === 0} onClick={exportCsv}>CSV</button>
        </div>
      </header>
      <p className="permission-note">모든 설정 변경과 지시 발행이 기록됩니다. 조회만 가능하며 수정·삭제할 수 없습니다.</p>
      {loading && events.length === 0 ? <Loading layout="table" label="로그를 불러오는 중…" /> : <DataTable cols="170px minmax(110px, 1fr) minmax(150px, 1.4fr) minmax(180px, 2fr)" dense>
        <div className="data-row data-head"><span>시각</span><span>수행자</span><span>종류</span><span>내용</span></div>
        {visible.map((event) => <div className="data-row" key={event.id}>
          <span className="mono">{koStamp(event.occurredAt)}</span>
          <span>{actorName(event)}</span>
          <span><b>{auditActionText[event.action] || event.action}</b></span>
          <span className="log-detail"><span className="mono">{event.resourceType}:{event.resourceId.slice(0, 8)}</span><em className={`source-badge ${event.source.toLowerCase()}`}>{event.source}</em></span>
        </div>)}
      </DataTable>}
      {!loading && !error && visible.length === 0 && <Empty title={events.length === 0 ? "기록된 로그가 없습니다" : "조건에 맞는 로그가 없습니다"} copy={events.length === 0 ? "설정 변경과 지시 발행이 일어나면 여기에 쌓입니다." : "검색어나 표면 조건을 바꿔 보세요."} />}
      {nextCursor && <div className="load-more"><button className="secondary" disabled={loading} onClick={() => void load(nextCursor)}>{loading ? "불러오는 중…" : "이전 기록 더 보기"}</button></div>}
    </section>
  </>;
}
